import {
  TextField,
  Grid,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  IconButton,
} from '@mui/material';
import {
  Person, Business, Build, Place,
  Description, Upload, CheckCircle, AccessTime, Email,
  Shield, VerifiedUser, ArrowForward, Close, CameraAlt,
  Edit, ArrowBack, Logout, HeadsetMic, Phone, Info, Check,
} from '@mui/icons-material';
import { Form } from 'react-bootstrap';
import { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { citiesAPI, districtsAPI, verificationAPI } from '../services/api';
import { getStoredAuthUserJson } from '../utils/secureStorage';
import { getBaseUrl } from '../utils/config';
/* ─── Inline global styles ─────────────────────────────────── */
const GlobalStyle = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500;600&display=swap');

    *, *::before, *::after { box-sizing: border-box; }

    .av-root {
      min-height: 100vh;
      background: #f0f4f8;
      padding: 24px 16px 48px;
      font-family: 'DM Sans', sans-serif;
    }

    .av-wrapper { max-width: 1040px; margin: 0 auto; }

    /* ── Hero banner ── */
    .av-hero {
      background: linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #1a56db 100%);
      border-radius: 16px;
      padding: 28px 36px;
      margin-bottom: 20px;
      position: relative;
      overflow: hidden;
      color: #fff;
    }
    .av-hero::before {
      content: '';
      position: absolute;
      top: -60px; right: -60px;
      width: 220px; height: 220px;
      border-radius: 50%;
      background: rgba(255,255,255,0.04);
      pointer-events: none;
    }
    .av-hero-badge {
      display: inline-flex; align-items: center; gap: 6px;
      background: rgba(255,255,255,0.12);
      border: 1px solid rgba(255,255,255,0.2);
      border-radius: 100px;
      padding: 5px 12px;
      font-size: 11px; font-weight: 600;
      letter-spacing: 0.5px; text-transform: uppercase;
      margin-bottom: 10px;
    }
    .av-hero-title {
      font-family: 'DM Serif Display', serif;
      font-size: clamp(22px, 3vw, 30px);
      font-weight: 400;
      line-height: 1.15;
      margin: 0 0 8px;
    }
    .av-hero-sub {
      font-size: 14px; color: rgba(255,255,255,0.7);
      max-width: 520px; line-height: 1.5; margin: 0;
    }

    /* ── Layout: sticky sidebar + scrollable form ── */
    .av-layout {
      display: grid;
      grid-template-columns: 260px 1fr;
      gap: 20px;
      align-items: start;
    }
    @media (max-width: 768px) {
      .av-layout { grid-template-columns: 1fr; }
    }

    /* ── Left sidebar – stacks tightly ── */
    .av-sidebar {
      position: sticky;
      top: 24px;
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    /* ── Section cards ── */
    .av-card {
      background: #fff;
      border-radius: 14px;
      box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.05);
      overflow: hidden;
    }
    .av-card-header {
      padding: 14px 18px 12px;
      border-bottom: 1px solid #f1f5f9;
      display: flex; align-items: center; gap: 10px;
    }
    .av-card-icon {
      width: 32px; height: 32px; border-radius: 9px;
      background: #eff6ff;
      display: flex; align-items: center; justify-content: center;
      flex-shrink: 0;
    }
    .av-card-body { padding: 12px 18px 18px; }

    /* ── Info row ── */
    .av-info-row {
      display: flex; align-items: flex-start; gap: 10px;
      padding: 8px 0;
      border-bottom: 1px solid #f8fafc;
    }
    .av-info-row:last-child { border-bottom: none; }
    .av-info-icon {
      width: 26px; height: 26px; border-radius: 7px;
      background: #f1f5f9;
      display: flex; align-items: center; justify-content: center;
      flex-shrink: 0; margin-top: 1px;
    }
    .av-info-label { font-size: 10px; color: #94a3b8; font-weight: 600; text-transform: uppercase; letter-spacing: 0.4px; }
    .av-info-value { font-size: 13px; color: #1e293b; font-weight: 500; margin-top: 2px; }

    /* ── Account type chip ── */
    .av-type-chip {
      display: inline-flex; align-items: center;
      background: #eff6ff; color: #1d4ed8;
      border-radius: 6px; padding: 2px 8px;
      font-size: 11px; font-weight: 700;
      letter-spacing: 0.5px; text-transform: uppercase;
    }

    /* ── Checklist sidebar card ── */
    .av-checklist-card {
      background: linear-gradient(135deg, #f0fdf4, #ecfdf5);
      border: 1px solid #bbf7d0;
      border-radius: 14px;
      padding: 16px 18px;
    }
    .av-checklist-title {
      display: flex; align-items: center; gap: 7px;
      font-weight: 700; font-size: 13px; color: #166534;
      margin-bottom: 10px;
    }
    .av-req-item {
      display: flex; align-items: center; gap: 8px;
      padding: 4px 0; font-size: 12px; color: #166534;
    }
    .av-meta-row {
      margin-top: 12px; padding-top: 12px; border-top: 1px solid #d1fae5;
      display: flex; flex-direction: column; gap: 6px;
    }
    .av-meta-item {
      display: flex; align-items: center; gap: 6px;
      font-size: 12px; color: #15803d;
    }

    /* ── Help Center sidebar card ── */
    .av-help-card {
      background: linear-gradient(135deg, #f0f9ff, #e0f2fe);
      border: 1px solid #bae6fd;
      border-radius: 14px;
      padding: 16px 18px;
    }
    .av-help-title {
      display: flex; align-items: center; gap: 7px;
      font-weight: 700; font-size: 13px; color: #0369a1;
      margin-bottom: 6px;
    }
    .av-help-sub {
      font-size: 11px; color: #0284c7; line-height: 1.4; margin-bottom: 10px;
    }
    .av-help-item {
      display: flex; align-items: center; gap: 8px;
      padding: 4px 0; font-size: 12px; color: #0369a1; font-weight: 600;
      text-decoration: none; word-break: break-all;
    }
    .av-help-item:hover { text-decoration: underline; color: #0284c7; }

    /* ── Form fields ── */
    .av-field-wrapper { margin-bottom: 14px; }
    .av-field-wrapper:last-child { margin-bottom: 0; }

    /* ── Upload zones ── */
    .av-upload-dropzone {
      border: 2px dashed #dee2e6;
      border-radius: 10px;
      padding: 12px 16px;
      text-align: center;
      cursor: pointer;
      transition: all 0.2s ease;
      background: #f8f9fa;
      display: flex; align-items: center; gap: 12px;
    }
    .av-upload-dropzone:hover, .av-upload-dropzone.drag-over {
      border-color: #0d6efd;
      background: #e7f1ff;
    }
    .av-upload-icon {
      width: 36px; height: 36px; border-radius: 8px;
      background: #e9ecef;
      display: flex; align-items: center; justify-content: center;
      flex-shrink: 0;
      transition: all 0.2s ease;
    }
    .av-upload-dropzone:hover .av-upload-icon { background: #cfe2ff; color: #0d6efd; }
    .av-upload-text { text-align: left; }
    .av-upload-options {
      display: flex; flex-wrap: wrap; gap: 10px; margin-top: 10px;
    }
    .av-upload-option-btn {
      display: inline-flex; align-items: center; gap: 8px;
      padding: 10px 16px; border-radius: 8px;
      border: 1px solid #dee2e6; background: #fff;
      font-size: 13px; font-weight: 600; color: #374151;
      cursor: pointer; transition: all 0.2s ease;
    }
    .av-upload-option-btn:hover {
      border-color: #0d6efd; background: #e7f1ff; color: #0d6efd;
    }
    .av-upload-option-btn.capture { border-color: #10b981; color: #059669; }
    .av-upload-option-btn.capture:hover { background: #ecfdf5; border-color: #10b981; color: #047857; }

    /* ── Camera capture modal ── */
    .av-camera-modal {
      position: fixed; inset: 0; z-index: 9999;
      background: rgba(0,0,0,0.85);
      display: flex; flex-direction: column;
      align-items: center; justify-content: center;
      padding: 20px;
    }
    .av-camera-modal-inner {
      background: #fff; border-radius: 16px;
      overflow: hidden; max-width: 100%;
      width: 100%; max-height: 90vh;
      display: flex; flex-direction: column;
    }
    .av-camera-video-wrap {
      position: relative; background: #000;
      width: 100%; aspect-ratio: 4/3;
      max-height: 70vh;
    }
    .av-camera-video-wrap video {
      width: 100%; height: 100%; object-fit: cover;
      display: block;
    }
    .av-camera-actions {
      padding: 16px 20px;
      display: flex; gap: 12px; justify-content: center;
      flex-wrap: wrap;
    }
    .av-camera-btn {
      display: inline-flex; align-items: center; gap: 8px;
      padding: 12px 24px; border-radius: 10px;
      font-size: 14px; font-weight: 600;
      border: none; cursor: pointer;
      transition: opacity 0.2s;
    }
    .av-camera-btn-capture {
      background: #10b981; color: #fff;
    }
    .av-camera-btn-capture:hover { opacity: 0.9; }
    .av-camera-btn-close {
      background: #e5e7eb; color: #374151;
    }
    .av-camera-btn-close:hover { background: #d1d5db; }

    /* ── Section divider ── */
    .av-section-label {
      font-size: 13px; font-weight: 700; color: #0f172a;
      text-transform: uppercase; letter-spacing: 0.5px;
      margin: 20px 0 12px; padding-bottom: 6px;
      border-bottom: 2px solid #e2e8f0;
    }
    .av-section-label:first-child { margin-top: 0; }

    /* ── Submit area ── */
    .av-submit-area {
      margin-top: 16px;
      background: #fff;
      border-radius: 14px;
      padding: 18px 22px;
      box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.05);
      display: flex; align-items: center; justify-content: space-between;
      gap: 16px; flex-wrap: wrap;
    }
    .av-submit-btn {
      display: inline-flex; align-items: center; gap: 8px;
      background: linear-gradient(135deg, #1a56db, #1d4ed8);
      color: #fff; border: none; border-radius: 10px;
      padding: 11px 26px; font-size: 14px; font-weight: 600;
      font-family: 'DM Sans', sans-serif;
      cursor: pointer; transition: opacity 0.2s, transform 0.15s;
    }
    .av-submit-btn:hover:not(:disabled) { opacity: 0.92; transform: translateY(-1px); }
    .av-submit-btn:disabled { background: #e2e8f0; color: #94a3b8; cursor: not-allowed; transform: none; }
    .av-submit-btn-outline {
      background: transparent; color: #1a56db;
      border: 2px solid #1a56db;
    }
    .av-submit-btn-outline:hover:not(:disabled) { background: #eff6ff; }

    /* ── Verification states ── */
    .av-verify-card {
      background: #fff;
      border-radius: 20px;
      box-shadow: 0 4px 24px rgba(0,0,0,0.08);
      padding: 48px 40px;
      text-align: center;
      max-width: 420px;
      margin: 0 auto;
    }
    .av-verify-clock {
      width: 72px; height: 72px; border-radius: 50%;
      background: #fef3c7;
      display: flex; align-items: center; justify-content: center;
      margin: 0 auto 24px;
    }
    .av-verify-success {
      width: 72px; height: 72px; border-radius: 50%;
      background: #16a34a; color: #fff;
      display: flex; align-items: center; justify-content: center;
      margin: 0 auto 24px;
    }
    .av-loading-dots {
      display: flex; justify-content: center; gap: 6px;
      margin: 20px 0 12px;
    }
    .av-loading-dots span {
      width: 8px; height: 8px; border-radius: 50%;
      background: #1a56db;
      animation: av-bounce 1.4s ease-in-out infinite both;
    }
    .av-loading-dots span:nth-child(1) { animation-delay: -0.32s; }
    .av-loading-dots span:nth-child(2) { animation-delay: -0.16s; }
    @keyframes av-bounce {
      0%, 80%, 100% { transform: scale(0); }
      40% { transform: scale(1); }
    }
    .av-welcome-box {
      background: #dcfce7; border-radius: 12px;
      padding: 16px 20px; margin-top: 20px;
      display: flex; align-items: flex-start; gap: 12px; text-align: left;
    }

    /* ── Preview Stepper ── */
    .av-stepper-bar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      background: #ffffff;
      border-radius: 14px;
      padding: 16px 28px;
      margin-bottom: 20px;
      box-shadow: 0 1px 3px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03);
      border: 1px solid #f1f5f9;
    }
    .av-step-item {
      display: flex;
      align-items: center;
      gap: 12px;
      position: relative;
    }
    .av-step-badge {
      width: 34px;
      height: 34px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 13px;
      font-weight: 700;
      transition: all 0.2s ease;
    }
    .av-step-item.completed .av-step-badge {
      background: #dcfce7;
      color: #15803d;
    }
    .av-step-item.active .av-step-badge {
      background: #1a56db;
      color: #ffffff;
      box-shadow: 0 0 0 4px rgba(26, 86, 219, 0.18);
    }
    .av-step-item.pending .av-step-badge {
      background: #f1f5f9;
      color: #94a3b8;
    }
    .av-step-label {
      display: flex;
      flex-direction: column;
    }
    .av-step-num {
      font-size: 10px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #94a3b8;
    }
    .av-step-item.active .av-step-num {
      color: #1a56db;
    }
    .av-step-item.completed .av-step-num {
      color: #166534;
    }
    .av-step-title {
      font-size: 13px;
      font-weight: 600;
      color: #475569;
    }
    .av-step-item.active .av-step-title {
      color: #0f172a;
      font-weight: 700;
    }
    .av-step-divider {
      flex: 1;
      height: 2px;
      background: #e2e8f0;
      margin: 0 16px;
    }
    .av-step-divider.completed {
      background: #86efac;
    }

    /* ── Preview Grid & Cards ── */
    .av-preview-section {
      display: flex;
      flex-direction: column;
      gap: 20px;
      margin-bottom: 20px;
    }
    .av-preview-card-v2 {
      background: #ffffff;
      border-radius: 16px;
      box-shadow: 0 1px 3px rgba(0,0,0,0.05), 0 4px 16px rgba(0,0,0,0.03);
      overflow: hidden;
      border: 1px solid #f1f5f9;
      transition: border-color 0.2s ease, box-shadow 0.2s ease;
    }
    .av-preview-card-v2:hover {
      border-color: #cbd5e1;
      box-shadow: 0 4px 20px rgba(0,0,0,0.06);
    }
    .av-preview-card-header {
      padding: 16px 24px;
      background: linear-gradient(180deg, #fafcff 0%, #ffffff 100%);
      border-bottom: 1px solid #f1f5f9;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
    }
    .av-preview-card-title {
      display: flex;
      align-items: center;
      gap: 10px;
      font-size: 15px;
      font-weight: 700;
      color: #0f172a;
      margin: 0;
    }
    .av-preview-card-icon {
      width: 32px;
      height: 32px;
      border-radius: 8px;
      background: #eff6ff;
      color: #1a56db;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .av-preview-card-body {
      padding: 20px 24px;
    }
    .av-details-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      gap: 16px 20px;
    }
    .av-detail-item {
      display: flex;
      flex-direction: column;
      gap: 4px;
      background: #f8fafc;
      padding: 12px 14px;
      border-radius: 10px;
      border: 1px solid #f1f5f9;
    }
    .av-detail-label {
      font-size: 11px;
      font-weight: 600;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 0.4px;
    }
    .av-detail-value {
      font-size: 14px;
      font-weight: 600;
      color: #1e293b;
      word-break: break-word;
    }

    /* ── Document List & Item Card ── */
    .av-doc-list {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }
    .av-doc-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 14px 18px;
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      gap: 16px;
      flex-wrap: wrap;
      transition: all 0.15s ease;
    }
    .av-doc-item:hover {
      border-color: #cbd5e1;
      background: #ffffff;
      box-shadow: 0 2px 8px rgba(0,0,0,0.04);
    }
    .av-doc-left {
      display: flex;
      align-items: center;
      gap: 14px;
      min-width: 240px;
      flex: 1;
    }
    .av-doc-icon-badge {
      width: 42px;
      height: 42px;
      border-radius: 10px;
      background: #eff6ff;
      color: #1d4ed8;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }
    .av-doc-meta {
      display: flex;
      flex-direction: column;
      gap: 3px;
    }
    .av-doc-title {
      font-size: 14px;
      font-weight: 700;
      color: #0f172a;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .av-doc-req-tag {
      font-size: 10px;
      font-weight: 700;
      color: #dc2626;
      background: #fef2f2;
      padding: 1px 6px;
      border-radius: 4px;
      border: 1px solid #fee2e2;
    }
    .av-doc-opt-tag {
      font-size: 10px;
      font-weight: 600;
      color: #64748b;
      background: #f1f5f9;
      padding: 1px 6px;
      border-radius: 4px;
    }
    .av-doc-filename {
      font-size: 12px;
      color: #475569;
      font-weight: 500;
    }
    .av-doc-right {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-shrink: 0;
    }

    /* ── Status Pills ── */
    .av-status-pill {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      padding: 5px 12px;
      border-radius: 100px;
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.2px;
    }
    .av-status-pill.success {
      background: #dcfce7;
      color: #15803d;
      border: 1px solid #bbf7d0;
    }
    .av-status-pill.neutral {
      background: #f1f5f9;
      color: #64748b;
      border: 1px solid #e2e8f0;
    }

    /* ── Legal Consent Box ── */
    .av-consent-card {
      background: linear-gradient(180deg, #fafcff 0%, #f0f7ff 100%);
      border: 1.5px solid #bfdbfe;
      border-radius: 16px;
      padding: 22px 26px;
      margin-top: 20px;
      box-shadow: 0 4px 16px rgba(37, 99, 235, 0.04);
    }
    .av-consent-header {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 14px;
    }
    .av-consent-title {
      font-size: 15px;
      font-weight: 700;
      color: #1e3a8a;
      margin: 0;
    }
    .av-consent-item {
      background: #ffffff;
      border: 1px solid #dbeafe;
      border-radius: 12px;
      padding: 12px 16px;
      margin-bottom: 10px;
      transition: border-color 0.15s ease, box-shadow 0.15s ease;
      display: flex;
      align-items: flex-start;
    }
    .av-consent-item:last-child {
      margin-bottom: 0;
    }
    .av-consent-item:hover {
      border-color: #93c5fd;
      box-shadow: 0 2px 8px rgba(37, 99, 235, 0.06);
    }


    /* ── Form right card ── */
    .av-form-card {
      background: #fff;
      border-radius: 14px;
      box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.05);
      overflow: hidden;
    }
    .av-form-card-header {
      padding: 14px 20px 12px;
      border-bottom: 1px solid #f1f5f9;
      display: flex; align-items: center; gap: 10px;
      background: #fafcff;
    }
    .av-form-card-body { padding: 18px 20px 20px; }

    /* ── Responsive adjustments ── */
    @media (max-width: 600px) {
      .av-root { padding: 12px 10px 32px; }
      .av-hero { padding: 24px 20px; border-radius: 12px; }
      .av-sidebar { position: static; gap: 12px; }
      .av-card-body { padding: 12px 14px 14px; }
      .av-form-card-body { padding: 14px 16px 16px; }
      
      .av-submit-area { 
        flex-direction: column; 
        align-items: stretch; 
        padding: 16px; 
        text-align: center;
        gap: 16px;
      }
      .av-submit-area > div { text-align: center; }
      .av-submit-actions { 
        flex-direction: column !important; 
        width: 100%; 
        gap: 10px !important;
      }
      /* Only make buttons in the main submit area full width */
      .av-submit-area .av-submit-btn { justify-content: center; width: 100%; }
      
      /* Preview screen info rows stack vertically */
      .av-preview-card .av-info-row { flex-direction: column; gap: 4px; padding: 12px 0; }
      .av-preview-card .av-info-row > div:first-child { min-width: auto !important; width: 100%; font-size: 10px; }
      .av-preview-card .av-info-row > div:last-child { 
        justify-content: flex-start !important; 
        text-align: left !important; 
        width: 100%; 
        padding-left: 0;
      }
      
      /* Profile details keep horizontal layout with icons */
      .av-sidebar .av-info-row { flex-direction: row; align-items: center; gap: 12px; }
      .av-sidebar .av-info-icon { margin-top: 0; }
      .av-sidebar .av-card-header { padding: 12px 16px; }
      .av-sidebar .av-card-body { padding: 8px 16px 14px; }
      
      .av-upload-options { flex-direction: column; }
      .av-upload-option-btn { width: 100%; justify-content: center; }
      
      .av-verify-card { padding: 32px 20px; }
      .av-verify-clock, .av-verify-success { width: 60px; height: 60px; }
      .av-verify-clock svg, .av-verify-success svg { font-size: 32px !important; }
      
      .av-camera-modal-inner { border-radius: 0; height: 100%; max-height: 100vh; }
      .av-camera-video-wrap { aspect-ratio: auto; flex: 1; }
    }
  `}</style>
);

const VALID_FILE_TYPES = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
const MAX_FILE_SIZE = 10 * 1024 * 1024;
/** Max files per document upload field (each zone). */
const MAX_FILES_PER_UPLOAD = 2;

const hasAllowedExtension = (name = '', extensions = []) => {
  const lower = String(name).toLowerCase();
  return extensions.some((ext) => lower.endsWith(ext));
};

const isAllowedUploadFile = (file, acceptImagesOnly = false) => {
  if (!file || file.size > MAX_FILE_SIZE) return false;
  const mime = String(file.type || '').toLowerCase();
  const isImageByMime = mime.startsWith('image/');
  const isPdfByMime = mime === 'application/pdf';
  const isImageByExt = hasAllowedExtension(file.name, ['.png', '.jpg', '.jpeg']);
  const isPdfByExt = hasAllowedExtension(file.name, ['.pdf']);
  const isImage = isImageByMime || isImageByExt;
  const isPdf = isPdfByMime || isPdfByExt;
  if (acceptImagesOnly) return isImage;
  return isImage || isPdf;
};

/* ─── Camera capture modal – opens live camera ── */
const CameraCaptureModal = ({ open, onClose, onCapture, facingMode = 'environment', title = 'Capture from camera' }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setReady(false);
    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: facingMode === 'user' ? 'user' : 'environment' },
          audio: false,
        });
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => setReady(true);
        }
      } catch (err) {
        setError(err.message || 'Camera access denied');
        toast.error('Could not open camera. Please allow camera access or use "Choose file" instead.');
      }
    };
    startCamera();
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, [open, facingMode]);

  const handleCapture = () => {
    if (!videoRef.current || !streamRef.current || !ready) return;
    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], `capture-${Date.now()}.jpg`, { type: 'image/jpeg' });
        onCapture(file);
        onClose();
      },
      'image/jpeg',
      0.9
    );
  };

  if (!open) return null;
  return (
    <div
      className="av-camera-modal"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      tabIndex={-1}
      onMouseDown={(e) => handleDialogBackdropMouseDown(e, onClose)}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div className="av-camera-modal-inner" onMouseDown={stopEventPropagation}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', fontWeight: 600, fontSize: 15 }}>
          {title}
        </div>
        <div className="av-camera-video-wrap">
          {error ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#ef4444', fontSize: 14 }}>{error}</div>
          ) : (
            <video ref={videoRef} autoPlay playsInline muted />
          )}
        </div>
        <div className="av-camera-actions">
          <button type="button" className="av-camera-btn av-camera-btn-capture" onClick={handleCapture} disabled={!ready || !!error}>
            <CameraAlt style={{ fontSize: 20 }} /> Take photo
          </button>
          <button type="button" className="av-camera-btn av-camera-btn-close" onClick={onClose}>
            <Close style={{ fontSize: 18 }} /> Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

CameraCaptureModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCapture: PropTypes.func.isRequired,
  facingMode: PropTypes.oneOf(['user', 'environment']),
  title: PropTypes.string,
};

const getUploadFileKey = (file) => file?.id || `${file?.name || file?.displayName || 'file'}-${file?.lastModified || 0}-${file?.size || 0}`;

const handleDialogBackdropMouseDown = (event, onClose) => {
  if (event.target === event.currentTarget) onClose();
};

const stopEventPropagation = (event) => {
  event.stopPropagation();
};

const handleUploadZoneKeyDown = (event, openFilePicker) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    openFilePicker();
  }
};

const processFiles = (files, setter, acceptImagesOnly = false) => {
  const valid = files.filter((f) => {
    return isAllowedUploadFile(f, acceptImagesOnly);
  });
  if (valid.length < files.length) toast.warn('Some files were skipped. Use PDF, PNG, JPG (max 10MB each).');
  setter((prev) => {
    const room = Math.max(0, MAX_FILES_PER_UPLOAD - prev.length);
    const toAdd = valid.slice(0, room);
    if (valid.length > room) {
      toast.warn(`You can upload at most ${MAX_FILES_PER_UPLOAD} files in this field.`);
    }
    return [...prev, ...toAdd];
  });
};

const renderFormattedUploadLabel = (labelStr) => {
  if (!labelStr) return null;
  const isRequired = labelStr.includes('*');
  const cleanText = labelStr.replace('*', '').trim();
  return (
    <Form.Label style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6, display: 'block' }}>
      {cleanText} {isRequired && <span style={{ color: '#dc2626', fontWeight: 700, fontSize: 14 }}>*</span>}
    </Form.Label>
  );
};

const FileUploadZone = ({ label, sublabel, files, setFiles, onRemove, accept = '.pdf,.png,.jpg,.jpeg', imagesOnly = false }) => {
  const fileRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const handleChange = (e) => {
    processFiles(Array.from(e.target.files || []), setFiles, imagesOnly);
    if (fileRef.current) fileRef.current.value = '';
  };
  const handleCameraCapture = (file) => {
    if (file.size > MAX_FILE_SIZE) {
      toast.warn('Image too large. Max 10MB.');
      setCameraOpen(false);
      return;
    }
    setFiles((prev) => {
      if (prev.length >= MAX_FILES_PER_UPLOAD) {
        toast.warn(`You can upload at most ${MAX_FILES_PER_UPLOAD} files in this field.`);
        return prev;
      }
      return [...prev, file];
    });
    setCameraOpen(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    processFiles(Array.from(e.dataTransfer.files || []), setFiles, imagesOnly);
  };
  return (
    <Form.Group className="mb-3">
      {renderFormattedUploadLabel(label)}
      <CameraCaptureModal open={cameraOpen} onClose={() => setCameraOpen(false)} onCapture={handleCameraCapture} facingMode="environment" title="Capture document" />
      <div
        className={`av-upload-dropzone ${dragOver ? 'drag-over' : ''}`}
        role="group"
        aria-label={label}
        tabIndex={0}
        onDrop={handleDrop}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onKeyDown={(e) => handleUploadZoneKeyDown(e, () => fileRef.current?.click())}
      >
        <div className="av-upload-icon"><Upload style={{ fontSize: 18, color: '#6b7280' }} /></div>
        <div className="av-upload-text flex-grow-1">
          <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Add document</div>
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>{sublabel}</div>
          <div className="av-upload-options">
            <button type="button" className="av-upload-option-btn capture" onClick={() => setCameraOpen(true)}>
              <CameraAlt style={{ fontSize: 18 }} /> Capture from camera
            </button>
            <input ref={fileRef} type="file" className="d-none" multiple accept={accept} onChange={handleChange} />
            <button type="button" className="av-upload-option-btn" onClick={() => fileRef.current?.click()}>
              <Upload style={{ fontSize: 18 }} /> Choose file / Upload
            </button>
          </div>
        </div>
      </div>
      {files.length > 0 && (
        <div className="d-flex flex-wrap gap-2 mt-2">
          {files.map((f, i) => (
            <span key={getUploadFileKey(f)} className="badge bg-light text-dark border d-inline-flex align-items-center gap-1 px-2 py-1" style={{ fontSize: '0.75rem' }}>
              <Description style={{ fontSize: 13, color: '#0d6efd' }} />
              {(f.name || f.displayName || f.fileName || 'Document').length > 28 ? (f.name || f.displayName || f.fileName || 'Document').slice(0, 26) + '…' : (f.name || f.displayName || f.fileName || 'Document')}
              <button type="button" className="btn btn-link p-0 ms-1 text-muted" style={{ fontSize: '0.9rem', minWidth: 'auto' }} onClick={(e) => { e.stopPropagation(); onRemove(i); }}>
                <Close style={{ fontSize: 13 }} />
              </button>
            </span>
          ))}
        </div>
      )}
    </Form.Group>
  );
};

FileUploadZone.propTypes = {
  label: PropTypes.string.isRequired,
  sublabel: PropTypes.string,
  files: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.instanceOf(File), PropTypes.object])).isRequired,
  setFiles: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  accept: PropTypes.string,
  imagesOnly: PropTypes.bool,
};

const PhotoCaptureZone = ({ photo, setPhoto }) => {
  const fileRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (f && isAllowedUploadFile(f, false)) setPhoto(f);
    else if (f) toast.warn('Use image or PDF, max 10MB.');
    if (fileRef.current) fileRef.current.value = '';
  };
  const handleCameraCapture = (file) => {
    if (file.size <= MAX_FILE_SIZE) setPhoto(file);
    else toast.warn('Image too large. Max 10MB.');
    setCameraOpen(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f && isAllowedUploadFile(f, false)) setPhoto(f);
  };
  return (
    <Form.Group className="mb-3">
      {renderFormattedUploadLabel("Photo (Live capture or upload) *")}
      <CameraCaptureModal open={cameraOpen} onClose={() => setCameraOpen(false)} onCapture={handleCameraCapture} facingMode="user" title="Capture your photo" />
      <div
        className={`av-upload-dropzone ${dragOver ? 'drag-over' : ''}`}
        role="group"
        aria-label="Photo upload area"
        tabIndex={0}
        onDrop={handleDrop}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onKeyDown={(e) => handleUploadZoneKeyDown(e, () => fileRef.current?.click())}
      >
        <div className="av-upload-icon"><CameraAlt style={{ fontSize: 18, color: '#6b7280' }} /></div>
        <div className="av-upload-text flex-grow-1">
          <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Add your photo</div>
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>Capture live from camera or choose file. PDF allowed.</div>
          <div className="av-upload-options">
            <button type="button" className="av-upload-option-btn capture" onClick={() => setCameraOpen(true)}>
              <CameraAlt style={{ fontSize: 18 }} /> Capture from camera
            </button>
            <input ref={fileRef} type="file" className="d-none" accept="image/*,.pdf" onChange={handleFile} />
            <button type="button" className="av-upload-option-btn" onClick={() => fileRef.current?.click()}>
              <Upload style={{ fontSize: 18 }} /> Choose file / Upload
            </button>
          </div>
        </div>
      </div>
      {photo && (
        <div className="d-flex flex-wrap gap-2 mt-2">
          {photo && (
            <span className="badge bg-light text-dark border d-inline-flex align-items-center gap-1 px-2 py-1" style={{ fontSize: '0.75rem' }}>
              <Description style={{ fontSize: 13, color: '#0d6efd' }} />
              {(photo.name || photo.displayName || photo.fileName || 'Photo').length > 28 ? (photo.name || photo.displayName || photo.fileName || 'Photo').slice(0, 26) + '…' : (photo.name || photo.displayName || photo.fileName || 'Photo')}
              <button type="button" className="btn btn-link p-0 ms-1 text-muted" style={{ fontSize: '0.9rem', minWidth: 'auto' }} onClick={() => setPhoto(null)}>
                <Close style={{ fontSize: 13 }} />
              </button>
            </span>
          )}
        </div>
      )}
    </Form.Group>
  );
};

PhotoCaptureZone.propTypes = {
  photo: PropTypes.oneOfType([PropTypes.instanceOf(File), PropTypes.object]),
  setPhoto: PropTypes.func.isRequired,
};

const textFieldSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '9px', fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 700, color: '#0f172a' },
  '& label': { fontFamily: "'DM Sans', sans-serif", fontSize: 12, fontWeight: 700, color: '#1e293b' },
  '& label.Mui-focused': { color: '#1a56db', fontWeight: 700 },
  '& .MuiFormLabel-asterisk': { color: '#dc2626', fontWeight: 700 },
  '& .MuiInputBase-input': { fontWeight: 700, color: '#0f172a' },
  '& .MuiSelect-select': { fontWeight: 700, color: '#0f172a' },
};

function unwrapProfilePayload(raw) {
  if (raw == null || typeof raw !== 'object') return null;
  const root = raw.data && typeof raw.data === 'object' && !Array.isArray(raw.data) ? raw.data : raw;
  const nested = root.profile && typeof root.profile === 'object' ? root.profile : {};
  return { ...nested, ...root };
}

function resolveDocumentUrl(url) {
  const u = String(url || '').trim();
  if (!u) return '';
  if (/^https?:\/\//i.test(u) || /^data:/i.test(u)) return u;
  const base = getBaseUrl();
  if (base && u.startsWith('/')) {
    return `${String(base).replace(/\/$/, '')}${u}`;
  }
  return u;
}

function formatDocumentLabel(label) {
  const s = String(label || 'Document').trim() || 'Document';
  return s
    .replaceAll('_', ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .split(/\s+/)
    .map((w) => (w.length ? w[0].toUpperCase() + w.slice(1).toLowerCase() : ''))
    .join(' ')
    .trim();
}

function filenameFromUrl(url) {
  try {
    const path = String(url || '').split('?')[0];
    const seg = path.split('/').filter(Boolean).findLast(Boolean);
    return seg ? decodeURIComponent(seg) : '';
  } catch {
    return '';
  }
}

const IMAGE_FILE_SUFFIXES = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.svg'];

function fileKindFromUrl(url) {
  const path = String(url || '').split('?')[0].toLowerCase();
  if (path.endsWith('.pdf')) return 'pdf';
  if (IMAGE_FILE_SUFFIXES.some((suffix) => path.endsWith(suffix))) return 'image';
  return 'other';
}

const DOCUMENT_ARRAY_KEYS = ['documents', 'verificationDocuments', 'uploadedDocuments', 'documentList', 'files'];

function normalizeDocTypeKey(s) {
  return String(s || '')
    .toUpperCase()
    .replace(/\s+/g, '_')
    .replace(/[^A-Z0-9_]/g, '');
}

/** Maps Submission Summary row labels → API documentType keys (for matching profile docs) */
const SUMMARY_LABEL_TO_TYPE_KEYS = {
  'Address document': ['ADDRESS_PROOF', 'ADDRESS'],
  'Address document (optional)': ['ADDRESS_PROOF', 'ADDRESS'],
  'Company registration': ['COMPANY_REGISTRATION', 'COMPANY_REGISTRATION_DOCUMENT', 'PROOF_OF_BUSINESS_REGISTRATION', 'BUSINESS_REGISTRATION'],
  'National ID / Permit': ['NATIONAL_ID', 'NATIONAL_ID_OR_RESIDENCE_PERMIT', 'RESIDENCE_PERMIT', 'PASSPORT', 'GERMAN_NATIONAL_ID'],
  'Professional License Document (optional)': ['PROFESSIONAL_LICENSE'],
  'Driving License Document (optional)': ['PROFESSIONAL_LICENSE', 'DRIVING_LICENSE'],
  Photo: ['PHOTO', 'LIVE_PHOTO', 'PROFILE_PHOTO', 'AVATAR'],
  'Experience document': ['EXPERIENCE_PROOF', 'YEAR_OF_EXPERIENCE', 'EXPERIENCE_DOCUMENT'],
};

function findDocForSummaryRow(label, docs) {
  const keys = SUMMARY_LABEL_TO_TYPE_KEYS[label];
  if (!keys?.length || !docs?.length) return null;
  for (const d of docs) {
    const dk = normalizeDocTypeKey(d.documentTypeKey || d.documentTypeLabel || '');
    for (const k of keys) {
      if (!k) continue;
      if (dk === k || dk.includes(k) || k.includes(dk)) return d;
    }
  }
  return null;
}

function buildSummaryDocLookup(docs) {
  const map = {};
  for (const label of Object.keys(SUMMARY_LABEL_TO_TYPE_KEYS)) {
    const doc = findDocForSummaryRow(label, docs);
    if (doc) map[label] = doc;
  }
  return map;
}

function stripKeySuffixIgnoreCase(key, suffix) {
  const lower = key.toLowerCase();
  const suffixLower = suffix.toLowerCase();
  if (!lower.endsWith(suffixLower)) return key;
  return key.slice(0, key.length - suffix.length);
}

function appendProfileDocument(out, typeLabel, urlHint, meta = {}) {
  const full = resolveDocumentUrl(
    meta.documentUrl ||
    meta.documentURL ||
    meta.url ||
    meta.fileUrl ||
    meta.fileURL ||
    meta.uri ||
    meta.path ||
    urlHint
  );
  const thumb = resolveDocumentUrl(
    meta.bannerUrl || meta.bannerURL || meta.thumbnailUrl || meta.previewUrl || ''
  );
  const resolved = full || thumb;
  if (!resolved) return;
  const typeCaption = formatDocumentLabel(typeLabel);
  const rawType = meta.documentType || meta.type || typeLabel || '';
  const documentTypeKey = normalizeDocTypeKey(rawType) || normalizeDocTypeKey(typeLabel);
  if (out.some((x) => x.url === resolved && x.documentTypeKey === documentTypeKey)) return;
  const rawName =
    meta.fileName ||
    meta.file_name ||
    meta.originalFileName ||
    meta.originalFilename ||
    (meta.name && /\.[a-z0-9]{2,4}$/i.test(String(meta.name).trim()) ? String(meta.name).trim() : '') ||
    '';
  const nameFromUrl = filenameFromUrl(resolved);
  const fileName = String(rawName || '').trim() || nameFromUrl || typeCaption;
  const displayName = String(rawName || '').trim() || nameFromUrl || typeCaption;
  const thumbUrl = thumb || resolved;
  out.push({
    id: `${displayName}-${resolved}-${documentTypeKey}-${out.length}`,
    url: resolved,
    thumbUrl,
    fileName,
    displayName,
    documentTypeKey,
    documentTypeLabel: typeCaption,
  });
}

function getDocumentItemLabel(item) {
  return (
    item.documentType ||
    item.documentTypeName ||
    item.type ||
    item.name ||
    item.title ||
    item.label ||
    'Document'
  );
}

function collectDocumentArrays(profile, out) {
  for (const key of DOCUMENT_ARRAY_KEYS) {
    const arr = profile[key];
    if (!Array.isArray(arr)) continue;
    for (const item of arr) {
      if (item == null) continue;
      if (typeof item === 'string') {
        appendProfileDocument(out, 'Document', item, {});
        continue;
      }
      if (typeof item !== 'object') continue;
      appendProfileDocument(out, getDocumentItemLabel(item), null, item);
    }
  }
}

function collectStringUrlDocumentFields(profile, out) {
  for (const [k, v] of Object.entries(profile)) {
    if (typeof v !== 'string' || !v.trim()) continue;
    const lower = k.toLowerCase();
    if (lower.endsWith('documenturl')) {
      const label = stripKeySuffixIgnoreCase(k, 'documenturl').replaceAll('_', ' ').trim();
      appendProfileDocument(out, label || 'Document', v, {});
      continue;
    }
    if (lower.endsWith('bannerurl')) {
      const label = stripKeySuffixIgnoreCase(k, 'bannerurl').replaceAll('_', ' ').trim();
      appendProfileDocument(out, label || 'Document', v, { bannerUrl: v, documentUrl: v });
    }
  }
}

function extractProfileDocuments(p) {
  if (!p || typeof p !== 'object') return [];
  const out = [];
  collectDocumentArrays(p, out);
  collectStringUrlDocumentFields(p, out);

  if (p.document && typeof p.document === 'object' && !Array.isArray(p.document)) {
    const d = p.document;
    appendProfileDocument(out, d.documentType || d.type || 'Document', null, d);
  }

  return out;
}

function DocumentPreviewDialog({ viewer, onClose }) {
  const kind = viewer ? fileKindFromUrl(viewer.url) : 'other';

  return (
    <Dialog
      open={Boolean(viewer)}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          m: 0,
          p: 0,
          width: '100%',
          maxWidth: '100vw',
          maxHeight: '100vh',
          minHeight: '100%',
          bgcolor: 'transparent',
          boxShadow: 'none',
          overflow: 'visible',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(15, 23, 42, 0.72)',
            backdropFilter: 'blur(6px)',
          },
        },
      }}
      sx={{
        '& .MuiDialog-container': {
          alignItems: 'center',
          justifyContent: 'center',
        },
      }}
    >
      {viewer && kind === 'image' && (
        <Box
          sx={{
            position: 'relative',
            display: 'inline-block',
            maxWidth: 'min(96vw, 1200px)',
            maxHeight: '90vh',
            lineHeight: 0,
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 24px 64px rgba(0,0,0,0.45)',
            outline: '1px solid rgba(255,255,255,0.14)',
          }}
        >
          <Box
            component="img"
            src={viewer.url}
            alt={viewer.displayName || ''}
            sx={{
              display: 'block',
              maxWidth: 'min(96vw, 1200px)',
              maxHeight: '90vh',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              verticalAlign: 'top',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              borderRadius: '12px',
            }}
          >
            <IconButton
              onClick={onClose}
              aria-label="Close preview"
              size="small"
              sx={{
                pointerEvents: 'auto',
                position: 'absolute',
                top: 4,
                right: 4,
                zIndex: 2,
                color: '#fff',
                bgcolor: 'rgba(15, 23, 42, 0.55)',
                border: '1px solid rgba(255,255,255,0.22)',
                '&:hover': { bgcolor: 'rgba(15, 23, 42, 0.85)' },
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      )}

      {viewer && kind === 'pdf' && (
        <Box
          sx={{
            position: 'relative',
            width: 'min(96vw, 920px)',
            height: 'min(85vh, 780px)',
            bgcolor: '#fff',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 24px 64px rgba(0,0,0,0.35)',
            outline: '1px solid rgba(255,255,255,0.12)',
          }}
        >
          <Box
            component="iframe"
            title={viewer.displayName}
            src={viewer.url}
            sx={{
              display: 'block',
              width: '100%',
              height: '100%',
              border: 'none',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
            }}
          >
            <IconButton
              onClick={onClose}
              aria-label="Close preview"
              size="small"
              sx={{
                pointerEvents: 'auto',
                position: 'absolute',
                top: 4,
                right: 4,
                zIndex: 2,
                color: '#fff',
                bgcolor: 'rgba(15, 23, 42, 0.55)',
                border: '1px solid rgba(255,255,255,0.22)',
                '&:hover': { bgcolor: 'rgba(15, 23, 42, 0.85)' },
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      )}

      {viewer && kind === 'other' && (
        <Box
          sx={{
            position: 'relative',
            bgcolor: '#fff',
            borderRadius: '12px',
            p: 3,
            maxWidth: 400,
            boxShadow: '0 12px 48px rgba(0,0,0,0.2)',
          }}
        >
          <IconButton
            onClick={onClose}
            aria-label="Close"
            size="small"
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              color: '#64748b',
            }}
          >
            <Close fontSize="small" />
          </IconButton>
          <Typography sx={{ mb: 2, pr: 4, fontSize: 14, color: '#334155' }}>
            Preview is not available for this file type.
          </Typography>
          <Typography
            component="a"
            href={viewer.url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: '#1a56db',
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            Open file in new tab
          </Typography>
        </Box>
      )}
    </Dialog>
  );
}

DocumentPreviewDialog.propTypes = {
  viewer: PropTypes.shape({
    url: PropTypes.string,
    displayName: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
};

const AV_VERIFICATION_STEP_KEY = 'av_verificationStep';

const readStoredVerificationStep = () => {
  try {
    const s = sessionStorage.getItem(AV_VERIFICATION_STEP_KEY);
    if (s === 'inProgress' || s === 'complete' || s === 'preview') return s;
  } catch {
    // ignore
  }
  return 'form';
};

const VERIFICATION_TRIPLE_KEYS = ['isDocumentVerified', 'complianceChecked', 'isFullyVerified'];

const isTrueishVerificationFlag = (v) => {
  const str = (x) => (x === undefined || x === null ? '' : String(x).toUpperCase());
  return v === true || v === 1 || str(v) === 'TRUE' || str(v) === 'VERIFIED' || str(v) === 'APPROVED';
};

/** When the status payload includes the verification triple, all three must be true to show the success state. */
const isBackendVerificationComplete = (status, legacyFullyVerified) => {
  if (!status || typeof status !== 'object') return Boolean(legacyFullyVerified);
  const allTriplePresent = VERIFICATION_TRIPLE_KEYS.every((k) => Object.prototype.hasOwnProperty.call(status, k));
  if (allTriplePresent) {
    return VERIFICATION_TRIPLE_KEYS.every((k) => isTrueishVerificationFlag(status[k]));
  }
  return Boolean(legacyFullyVerified);
};

const hasVerificationTripleContract = (status) =>
  Boolean(status && typeof status === 'object' && VERIFICATION_TRIPLE_KEYS.some((k) => Object.prototype.hasOwnProperty.call(status, k)));

/* ─── Component ─────────────────────────────────────────────── */
const AccountVerification = () => {
  const navigate = useNavigate();
  const { logout, isAuthenticated } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  const [signupData, setSignupData] = useState(null);
  const [step, setStep] = useState(readStoredVerificationStep);
  const [termsAndConditionsAccepted, setTermsAndConditionsAccepted] = useState(false);
  const [dataProcessingConsent, setDataProcessingConsent] = useState(false);
  const canConfirmSubmission = termsAndConditionsAccepted && dataProcessingConsent;
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [statusPolling, setStatusPolling] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [docsSubmitting, setDocsSubmitting] = useState(false);
  /** After first successful submit-to-preview, use PUT update on subsequent saves (edit → preview). */
  const [documentsSavedOnce, setDocumentsSavedOnce] = useState(() => {
    try {
      return sessionStorage.getItem('av_documentsSubmittedOnce') === '1';
    } catch {
      return false;
    }
  });

  // Company form state
  const [addressDoc, setAddressDoc] = useState([]);
  const [city, setCity] = useState('');
  const [district, setDistrict] = useState('');
  const [cities, setCities] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [companyDistricts, setCompanyDistricts] = useState([]);
  const [freelancerDistricts, setFreelancerDistricts] = useState([]);
  const [companyDistrictsLoading, setCompanyDistrictsLoading] = useState(false);
  const [freelancerDistrictsLoading, setFreelancerDistrictsLoading] = useState(false);
  const [zipcode, setZipcode] = useState('');
  const [companyRegDoc, setCompanyRegDoc] = useState([]);
  const [ownerName, setOwnerName] = useState('');
  const [nationalIdDoc, setNationalIdDoc] = useState([]);
  const [licenseIssuedState, setLicenseIssuedState] = useState('');
  const [professionalLicenseDoc, setProfessionalLicenseDoc] = useState([]);
  const [companyDescription, setCompanyDescription] = useState('');
  const [webUrl, setWebUrl] = useState('');

  // Freelancer form state
  const [photo, setPhoto] = useState(null);
  const [freelancerAddressDoc, setFreelancerAddressDoc] = useState([]);
  const [freelancerCity, setFreelancerCity] = useState('');
  const [freelancerDistrict, setFreelancerDistrict] = useState('');
  const [freelancerZipcode, setFreelancerZipcode] = useState('');
  const [freelancerNationalIdDoc, setFreelancerNationalIdDoc] = useState([]);
  const [yearOfExpDoc, setYearOfExpDoc] = useState([]);
  const [freelancerLicenseIssuedState, setFreelancerLicenseIssuedState] = useState('');
  const [freelancerProfessionalLicenseDoc, setFreelancerProfessionalLicenseDoc] = useState([]);
  const [serviceDescription, setServiceDescription] = useState('');
  const [freelancerWebUrl, setFreelancerWebUrl] = useState('');

  const profileUploadedDocs = useMemo(() => extractProfileDocuments(profileData), [profileData]);
  const [verificationPreviewDoc, setVerificationPreviewDoc] = useState(null);

  useEffect(() => {
    if (profileData && extractProfileDocuments(profileData).length > 0) {
      setDocumentsSavedOnce(true);
      try {
        sessionStorage.setItem('av_documentsSubmittedOnce', '1');
      } catch {
        // ignore
      }
    }
  }, [profileData]);

  useEffect(() => {
    if (step !== 'preview') setVerificationPreviewDoc(null);
  }, [step]);

  useEffect(() => {
    try {
      sessionStorage.setItem(AV_VERIFICATION_STEP_KEY, step);
    } catch {
      // ignore
    }
  }, [step]);

  useEffect(() => {
    const stored = sessionStorage.getItem('signupData');
    if (!stored) return;
    try {
      setSignupData(JSON.parse(stored));
    } catch {
      // ignore; UI will fallback to pending screen
    }
  }, [navigate]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  // Load authenticated profile for sidebar (uses auth token from localStorage via axios interceptor).
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    const loadProfile = async () => {
      setProfileLoading(true);
      try {
        const response = await verificationAPI.getProfile();
        const raw = response?.data?.data ?? response?.data ?? null;
        const payload = unwrapProfilePayload(raw) ?? raw;
        if (!cancelled) setProfileData(payload);
      } catch (error) {
        console.warn('Failed to load profile for sidebar:', error);
      } finally {
        if (!cancelled) setProfileLoading(false);
      }
    };
    loadProfile();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  // When user arrives here without signupData (login pending redirect),
  // we will determine which step to show from the backend status below.

  // Load cities once for the address dropdowns
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    const loadCities = async () => {
      setCitiesLoading(true);
      try {
        const response = await citiesAPI.getCities();
        const citiesList = response?.data?.payload || response?.data?.data || response?.data || [];
        if (!cancelled) setCities(Array.isArray(citiesList) ? citiesList : []);
      } catch (error) {
        console.error('Failed to load cities:', error);
        if (!cancelled) setCities([]);
      } finally {
        if (!cancelled) setCitiesLoading(false);
      }
    };
    loadCities();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (step !== 'inProgress') return;
    if (!isAuthenticated) return;

    let cancelled = false;
    let timeoutId = null;

    const str = (v) => (v === undefined || v === null ? '' : String(v).toUpperCase());
    const isTrueish = (v) => v === true || v === 1 || str(v) === 'TRUE' || str(v) === 'VERIFIED' || str(v) === 'APPROVED';

    const poll = async () => {
      try {
        const email = getStatusEmail();
        if (!email) return;
        const resp = await verificationAPI.getStatus({ email });
        const payload = resp?.data?.data ?? resp?.data ?? {};
        setVerificationStatus(payload);

        const emailVerified =
          payload?.emailVerified ?? payload?.isEmailVerified ?? payload?.email?.verified ?? isTrueish(payload?.emailVerified);
        const phoneVerified =
          payload?.phoneVerified ?? payload?.isPhoneVerified ?? payload?.phone?.verified ?? isTrueish(payload?.phoneVerified);
        const documentsVerified =
          payload?.isDocumentUploaded ??
          payload?.documentsVerified ??
          payload?.isDocumentsVerified ??
          payload?.documents?.verified ??
          payload?.documentVerificationStatus ??
          isTrueish(payload?.documentsVerified);

        const legacyFullyVerified =
          payload?.fullyVerified ??
          payload?.verificationComplete ??
          payload?.isFullyVerified ??
          (emailVerified && phoneVerified && documentsVerified);

        if (!cancelled && isBackendVerificationComplete(payload, legacyFullyVerified)) {
          setStep('complete');
          return;
        }
      } catch (error) {
        console.warn('Verification status poll failed:', error);
      }

      if (!cancelled) {
        timeoutId = setTimeout(poll, 3000);
      }
    };

    setStatusPolling(true);
    poll();

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
      setStatusPolling(false);
    };
  }, [step, isAuthenticated]);

  useEffect(() => {
    // For users who come from login and have pending verification,
    // decide the correct pending screen from verification status.
    // Only skip when still filling the signup form; after submit (inProgress/preview/complete)
    // we must run so refresh can reconcile with the API even if signupData exists in session.
    if (signupData && step === 'form') return;
    if (step === 'complete') return;
    if (step === 'preview') return;
    // While pending, only the inProgress poller may advance to complete — never send user back to document upload.
    if (step === 'inProgress') return;
    if (!isAuthenticated) return;

    let cancelled = false;
    const str = (v) => (v === undefined || v === null ? '' : String(v).toUpperCase());
    const isTrueish = (v) => v === true || v === 1 || str(v) === 'TRUE' || str(v) === 'VERIFIED' || str(v) === 'APPROVED';

    const run = async () => {
      try {
        const email = getStatusEmail();
        if (!email) return;
        const [statusResp, profileResp] = await Promise.allSettled([
          verificationAPI.getStatus({ email }),
          verificationAPI.getProfile(),
        ]);

        const statusPayload = statusResp.status === 'fulfilled' ? statusResp.value?.data : null;
        const profilePayload = profileResp.status === 'fulfilled' ? profileResp.value?.data : null;
        const status = statusPayload?.data ?? statusPayload ?? {};
        const profileRaw = profilePayload?.data ?? profilePayload ?? {};
        const profile = unwrapProfilePayload(profileRaw) ?? profileRaw;

        if (!cancelled) setProfileData(profile);
        if (!cancelled) setVerificationStatus(status);

        const emailVerified = status?.emailVerified ?? status?.isEmailVerified ?? status?.email?.verified ?? isTrueish(profile?.emailVerified);
        const phoneVerified = status?.phoneVerified ?? status?.isPhoneVerified ?? status?.phone?.verified ?? isTrueish(profile?.phoneVerified);
        const isSubmissionConfirmed =
          isTrueish(status?.isSubmissionConfirmed) ||
          isTrueish(profile?.isSubmissionConfirmed);

        const documentsVerified =
          status?.isDocumentUploaded ??
          status?.documentsVerified ??
          status?.isDocumentsVerified ??
          status?.documents?.verified ??
          status?.documentVerificationStatus ??
          isTrueish(profile?.documentsVerified);

        const legacyFullyVerified =
          status?.fullyVerified ??
          status?.verificationComplete ??
          status?.isFullyVerified ??
          (emailVerified && phoneVerified && documentsVerified);

        if (isBackendVerificationComplete(status, legacyFullyVerified) && !cancelled) {
          setStep('complete');
          return;
        }
        if (!emailVerified && !cancelled) {
          const email = profile?.email || '';
          const phone = profile?.phone || profile?.phoneNumber || '';
          navigate(`/verify?step=email&email=${encodeURIComponent(email)}&phone=${encodeURIComponent(phone)}`);
          return;
        }
        if (!phoneVerified && !cancelled) {
          const email = profile?.email || '';
          const phone = profile?.phone || profile?.phoneNumber || '';
          navigate(`/verify?step=phone&email=${encodeURIComponent(email)}&phone=${encodeURIComponent(phone)}`);
          return;
        }

        if (step === 'form') {
          return;
        }

        if (!isSubmissionConfirmed && !cancelled) {
          setStep('preview');
          return;
        }

        const statusCode =
          status?.statusCode ??
          status?.verificationStatus?.statusCode ??
          profile?.verificationStatus?.statusCode ??
          profile?.statusCode ??
          null;

        const docUploadStage =
          statusCode === 4 ||
          str(status?.status) === 'DOCUMENT_UPLOAD' ||
          str(profile?.verificationStatus?.status) === 'DOCUMENT_UPLOAD';

        if (hasVerificationTripleContract(status)) {
          const complianceDone = isTrueishVerificationFlag(status.complianceChecked);
          if (complianceDone && !isBackendVerificationComplete(status, legacyFullyVerified) && !cancelled) {
            navigateToVerifyPending();
            return;
          }
          if (!complianceDone && docUploadStage && !cancelled) {
            setStep('form');
            if (profile) applyProfileToVerificationForm(profile);
            return;
          }
          if (!complianceDone && !cancelled) {
            const hasDocsOnProfile = extractProfileDocuments(profile).length > 0;
            if (hasDocsOnProfile) {
              setDocumentsSavedOnce(true);
              try {
                sessionStorage.setItem('av_documentsSubmittedOnce', '1');
              } catch {
                // ignore
              }
              setStep('preview');
              return;
            }
            setStep('form');
            if (profile) applyProfileToVerificationForm(profile);
            return;
          }
        }

        if (!cancelled && docUploadStage) {
          setStep('form');
          if (profile) applyProfileToVerificationForm(profile);
          return;
        }

        // Email and phone are verified, but document verification is pending/review.
        if (!cancelled) navigateToVerifyPending();
      } catch {
        if (!cancelled) navigateToVerifyPending();
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [signupData, step, navigate, isAuthenticated]);

  const resolvedAccountType = profileData?.accountType || profileData?.userType || profileData?.type || signupData?.accountType || '';
  const isCompany = String(resolvedAccountType).toLowerCase() === 'company';
  const displayName = isCompany
    ? profileData?.businessName || profileData?.companyName || signupData?.businessName || ''
    : profileData?.name || profileData?.fullName || signupData?.name || '';
  const profileEmail = profileData?.email || signupData?.email || '';
  const profileServiceType =
    profileData?.contactTypeLabel ||
    profileData?.serviceTypeLabel ||
    profileData?.contactTypeName ||
    profileData?.categoryName ||
    signupData?.contactTypeLabel ||
    signupData?.contactType ||
    profileData?.contactTypeId ||
    profileData?.categoryId ||
    '—';
  const profileCountry = profileData?.country || signupData?.country || '';

  const storedUser = useMemo(() => {
    try {
      return getStoredAuthUserJson() || {};
    } catch {
      return {};
    }
  }, []);

  const displayOwnerName =
    ownerName ||
    profileData?.ownerName ||
    profileData?.companyOwnerName ||
    profileData?.owner_name ||
    profileData?.company_owner_name ||
    profileData?.owner ||
    signupData?.ownerName ||
    signupData?.companyOwnerName ||
    signupData?.owner_name ||
    storedUser?.ownerName ||
    storedUser?.companyOwnerName ||
    storedUser?.owner_name ||
    profileData?.name ||
    profileData?.fullName ||
    signupData?.name ||
    signupData?.fullName ||
    storedUser?.name ||
    storedUser?.fullName ||
    '';

  const displayZipcode =
    (isCompany ? zipcode : freelancerZipcode) ||
    profileData?.company_Zipcode ||
    profileData?.company_zipCode ||
    profileData?.companyZipcode ||
    profileData?.companyZipCode ||
    profileData?.Company_Zipcode ||
    profileData?.Company_zipCode ||
    profileData?.CompanyZipcode ||
    profileData?.freelancer_Zipcode ||
    profileData?.freelancer_zipCode ||
    profileData?.freelancerZipcode ||
    profileData?.freelancerZipCode ||
    profileData?.Freelancer_Zipcode ||
    profileData?.zipcode ||
    profileData?.zipCode ||
    profileData?.zip_code ||
    profileData?.postalCode ||
    profileData?.postal_code ||
    profileData?.pincode ||
    profileData?.pin_code ||
    profileData?.zip ||
    signupData?.company_Zipcode ||
    signupData?.company_zipCode ||
    signupData?.companyZipcode ||
    signupData?.companyZipCode ||
    signupData?.freelancer_Zipcode ||
    signupData?.freelancer_zipCode ||
    signupData?.freelancerZipcode ||
    signupData?.freelancerZipCode ||
    signupData?.zipcode ||
    signupData?.zipCode ||
    signupData?.zip_code ||
    signupData?.postalCode ||
    signupData?.postal_code ||
    signupData?.pincode ||
    signupData?.pin_code ||
    signupData?.zip ||
    storedUser?.companyZipcode ||
    storedUser?.freelancerZipcode ||
    storedUser?.zipcode ||
    storedUser?.zipCode ||
    storedUser?.zip_code ||
    storedUser?.postalCode ||
    storedUser?.postal_code ||
    '';

  // Auto-select the first city once cities are loaded
  useEffect(() => {
    if (!Array.isArray(cities) || cities.length === 0) return;

    if (isCompany) {
      if (city === '' || city === null || city === undefined) {
        setCity(Number(cities[0].id));
      }
    } else if (freelancerCity === '' || freelancerCity === null || freelancerCity === undefined) {
      setFreelancerCity(Number(cities[0].id));
    }
  }, [cities, isCompany, city, freelancerCity]);

  // Fetch districts when company city changes
  useEffect(() => {
    if (!isCompany) return;

    if (city === '' || city === null || city === undefined) {
      setCompanyDistricts([]);
      return;
    }

    let cancelled = false;
    const run = async () => {
      setCompanyDistrictsLoading(true);
      try {
        const response = await districtsAPI.getDistrictss(city);
        const list = response?.data?.payload || response?.data?.data || response?.data || [];
        const normalized = Array.isArray(list) ? list : [];

        if (!cancelled) {
          setCompanyDistricts(normalized);
          if (
            normalized.length > 0 &&
            (district === '' || district === null || district === undefined)
          ) {
            setDistrict(Number(normalized[0].id));
          }
        }
      } catch (error) {
        console.error('Failed to load company districts:', error);
        if (!cancelled) setCompanyDistricts([]);
      } finally {
        if (!cancelled) setCompanyDistrictsLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [isCompany, city, district]);

  // Fetch districts when freelancer city changes
  useEffect(() => {
    if (isCompany) return;

    if (freelancerCity === '' || freelancerCity === null || freelancerCity === undefined) {
      setFreelancerDistricts([]);
      return;
    }

    let cancelled = false;
    const run = async () => {
      setFreelancerDistrictsLoading(true);
      try {
        const response = await districtsAPI.getDistrictss(freelancerCity);
        const list = response?.data?.payload || response?.data?.data || response?.data || [];
        const normalized = Array.isArray(list) ? list : [];

        if (!cancelled) {
          setFreelancerDistricts(normalized);
          if (
            normalized.length > 0 &&
            (freelancerDistrict === '' || freelancerDistrict === null || freelancerDistrict === undefined)
          ) {
            setFreelancerDistrict(Number(normalized[0].id));
          }
        }
      } catch (error) {
        console.error('Failed to load freelancer districts:', error);
        if (!cancelled) setFreelancerDistricts([]);
      } finally {
        if (!cancelled) setFreelancerDistrictsLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [isCompany, freelancerCity, freelancerDistrict]);

  const companyValid = () =>
    city !== '' && district !== '' && (zipcode || displayZipcode).trim() &&
    companyRegDoc.length > 0 &&
    (ownerName || displayOwnerName).trim() && nationalIdDoc.length > 0;

  const freelancerValid = () =>
    photo && freelancerCity !== '' &&
    freelancerDistrict !== '' && (freelancerZipcode || displayZipcode).trim() &&
    freelancerNationalIdDoc.length > 0 && yearOfExpDoc.length > 0;

  const canProceedToPreview = isCompany ? companyValid() : freelancerValid();

  const getStatusEmail = () => {
    const fromProfile = profileData?.email;
    const fromSignup = signupData?.email;
    let fromLocal = '';
    try {
      const parsed = getStoredAuthUserJson();
      fromLocal = parsed?.email || '';
    } catch {
      fromLocal = '';
    }
    return fromProfile || fromSignup || fromLocal || '';
  };

  /** Server-side document review (isDocumentVerified false) — same UI as /verify?step=pending */
  const navigateToVerifyPending = () => {
    const em = getStatusEmail();
    if (em) {
      navigate(`/verify?step=pending&email=${encodeURIComponent(em)}`);
    } else {
      setStep('inProgress');
    }
  };

  const buildDocumentsFormData = () => {
    const formData = new FormData();
    const appendDocWithType = (file, type, aliases = []) => {
      if (!file) return;
      if (file instanceof File) {
        // Generic typed format (some backends read documents + documentType(s))
        formData.append('documents', file);
        formData.append('documentType', type);
        formData.append('documentTypes', type);
        // Legacy/explicit keys
        aliases.forEach((k) => formData.append(k, file));
      } else if (typeof file === 'object') {
        if (file.url) {
          formData.append('documentUrl', file.url);
          aliases.forEach((k) => formData.append(`${k}Url`, file.url));
        }
      }
    };

    if (isCompany) {
      formData.append('cityId', city);
      formData.append('districtId', district);
      formData.append('zipcode', zipcode || displayZipcode);
      formData.append('ownerName', ownerName || displayOwnerName);
      formData.append('companyDescription', companyDescription);
      formData.append('webUrl', webUrl);
      formData.append('licenseIssuedState', licenseIssuedState);
      formData.append('accountType', isCompany ? 'COMPANY' : 'FREELANCER');
      addressDoc.forEach((f) => appendDocWithType(f, 'ADDRESS_PROOF', ['addressDoc']));
      companyRegDoc.forEach((f) => appendDocWithType(f, 'COMPANY_REGISTRATION', ['companyRegDoc']));
      nationalIdDoc.forEach((f) => {
        appendDocWithType(f, 'NATIONAL_ID_OR_RESIDENCE_PERMIT', ['nationalIdDoc']);
        // Fallback to satisfy overly strict backend requiring all types regardless of account type
        appendDocWithType(f, 'PHOTO', ['photo']);
        appendDocWithType(f, 'EXPERIENCE_PROOF', ['experienceProofDoc', 'yearOfExpDoc']);
      });
      professionalLicenseDoc.forEach((f) => appendDocWithType(f, 'PROFESSIONAL_LICENSE', ['professionalLicenseDoc']));
    } else {
      formData.append('cityId', freelancerCity);
      formData.append('districtId', freelancerDistrict);
      formData.append('zipcode', freelancerZipcode || displayZipcode);
      formData.append('serviceDescription', serviceDescription);
      formData.append('webUrl', freelancerWebUrl);
      formData.append('accountType', isCompany ? 'COMPANY' : 'FREELANCER');
      if (photo) appendDocWithType(photo, 'PHOTO', ['photo']);
      // Backend required types are generic (ADDRESS_PROOF, NATIONAL_ID..., TAX_ID..., EXPERIENCE_PROOF).
      // Send both generic and legacy freelancer-specific keys for compatibility.
      freelancerAddressDoc.forEach((f) => {
        appendDocWithType(f, 'ADDRESS_PROOF', ['addressDoc', 'freelancerAddressDoc']);
      });
      freelancerNationalIdDoc.forEach((f) => {
        appendDocWithType(f, 'NATIONAL_ID_OR_RESIDENCE_PERMIT', ['nationalIdDoc', 'freelancerNationalIdDoc']);
        // Fallback to satisfy overly strict backend requiring all types regardless of account type
        appendDocWithType(f, 'COMPANY_REGISTRATION', ['companyRegDoc']);
        appendDocWithType(f, 'COMPANY_LEGAL_FORM', ['companyLegalFormDoc']);
      });
      yearOfExpDoc.forEach((f) => {
        appendDocWithType(f, 'EXPERIENCE_PROOF', ['yearOfExpDoc', 'experienceProofDoc']);
      });
      freelancerProfessionalLicenseDoc.forEach((f) =>
        appendDocWithType(f, 'PROFESSIONAL_LICENSE', ['freelancerProfessionalLicenseDoc'])
      );
      formData.append('licenseIssuedState', freelancerLicenseIssuedState);
    }
    return formData;
  };

  const verificationApiErrorMessage = (e) => {
    const d = e?.response?.data;
    if (!d) return e?.message || 'Request failed';
    if (typeof d.error === 'string') return d.error;
    if (d.error?.message) return d.error.message;
    return d.message || d.errorMessage || e?.message || 'Request failed';
  };

  /** PUT /documents/update when nothing on server yet — fall back to POST /documents/submit. */
  const isNoExistingDocumentsError = (e) => {
    const d = e?.response?.data;
    if (!d || typeof d !== 'object') return false;
    const code = d.error?.code ?? d.code ?? d.errorCode;
    if (code === 'NO_EXISTING_DOCUMENTS') return true;
    const msg = String(d.error?.message || d.message || '').toLowerCase();
    return msg.includes('no existing documents') && msg.includes('submit');
  };

  const handleProceedToPreview = () => {
    if (!canProceedToPreview) return;
    setStep('preview');
  };

  const handleSubmit = () => {
    if (!canConfirmSubmission) {
      toast.error('Please accept Terms & Conditions and Data Processing Consent.');
      return;
    }

    const run = async () => {
      setDocsSubmitting(true);
      try {
        const fd = buildDocumentsFormData();
        await verificationAPI.submitDocuments(fd);

        setDocumentsSavedOnce(true);
        try {
          sessionStorage.setItem('av_documentsSubmittedOnce', '1');
        } catch {
          // ignore
        }

        // Confirm final consent
        await verificationAPI.confirmSubmission({
          termsAndConditionsAccepted,
          dataProcessingConsent,
        });

        try {
          const st = await verificationAPI.getStatus({ email: getStatusEmail() });
          const payload = st?.data?.data ?? st?.data ?? {};
          setVerificationStatus(payload);
          if (isBackendVerificationComplete(payload, payload?.fullyVerified)) {
            setStep('complete');
            return;
          }
        } catch {
          // status is optional; inProgress polling will refresh
        }

        toast.success('Verification submitted successfully.', { position: 'top-center' });
        navigateToVerifyPending();
      } catch (e) {
        const msg = verificationApiErrorMessage(e);
        toast.error(msg, { position: 'top-center' });
        console.warn('Verification submit error:', e);
      } finally {
        setDocsSubmitting(false);
      }
    };

    run();
  };

  const handleViewDocuments = async () => {
    setProfileLoading(true);
    try {
      const resp = await verificationAPI.getProfile();
      const raw = resp?.data?.data ?? resp?.data ?? resp;
      const payload = unwrapProfilePayload(raw) ?? raw;
      setProfileData(payload || null);
      return payload || null;
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data?.errorMessage || e?.message || 'Failed to load profile';
      toast.error(msg);
      return null;
    } finally {
      setProfileLoading(false);
    }
  };

  const applyProfileToVerificationForm = (profile) => {
    if (!profile || typeof profile !== 'object') return;

    const pick = (...vals) => vals.find((v) => v !== undefined && v !== null && String(v) !== '');
    const toIdOrEmpty = (v) => (v === undefined || v === null || v === '' ? '' : String(v));

    const companyCityVal = toIdOrEmpty(
      pick(
        profile.company_CityId,
        profile.company_cityId,
        profile.companyCityId,
        profile.Company_CityId,
        profile.cityId,
        profile.city?.id
      )
    );
    if (companyCityVal) setCity(companyCityVal);

    const companyDistVal = toIdOrEmpty(
      pick(
        profile.company_DistrictId,
        profile.company_districtId,
        profile.companyDistrictId,
        profile.Company_DistrictId,
        profile.districtId,
        profile.district?.id
      )
    );
    if (companyDistVal) setDistrict(companyDistVal);

    const resolvedCompanyZip = String(
      pick(
        profile.company_Zipcode,
        profile.company_zipCode,
        profile.companyZipcode,
        profile.companyZipCode,
        profile.Company_Zipcode,
        profile.Company_zipCode,
        profile.CompanyZipcode,
        profile.zipcode,
        profile.zipCode,
        profile.zip_code,
        profile.postalCode,
        profile.postal_code,
        profile.pincode,
        profile.pin_code,
        profile.zip,
        ''
      ) || ''
    ).replace(/\D/g, '').slice(0, 5);
    if (resolvedCompanyZip) setZipcode(resolvedCompanyZip);

    const resolvedOwner = String(
      pick(
        profile.ownerName,
        profile.companyOwnerName,
        profile.owner_name,
        profile.company_owner_name,
        profile.owner,
        profile.name,
        profile.fullName,
        profile.businessName,
        ''
      ) || ''
    );
    if (resolvedOwner) setOwnerName(resolvedOwner);

    const compDesc = String(pick(profile.companyDescription, profile.description, '') || '');
    if (compDesc) setCompanyDescription(compDesc);

    const wUrl = String(pick(profile.webUrl, profile.website, profile.companyWebsite, '') || '');
    if (wUrl) setWebUrl(wUrl);

    const licState = String(pick(profile.licenseIssuedState, '') || '');
    if (licState) setLicenseIssuedState(licState);

    const freelancerCityVal = toIdOrEmpty(
      pick(
        profile.freelancer_CityId,
        profile.freelancer_cityId,
        profile.freelancerCityId,
        profile.Freelancer_CityId,
        profile.cityId,
        profile.city?.id
      )
    );
    if (freelancerCityVal) setFreelancerCity(freelancerCityVal);

    const freelancerDistVal = toIdOrEmpty(
      pick(
        profile.freelancer_DistrictId,
        profile.freelancer_districtId,
        profile.freelancerDistrictId,
        profile.Freelancer_DistrictId,
        profile.districtId,
        profile.district?.id
      )
    );
    if (freelancerDistVal) setFreelancerDistrict(freelancerDistVal);

    const resolvedFreelancerZip = String(
      pick(
        profile.freelancer_Zipcode,
        profile.freelancer_zipCode,
        profile.freelancerZipcode,
        profile.freelancerZipCode,
        profile.Freelancer_Zipcode,
        profile.Freelancer_zipCode,
        profile.FreelancerZipcode,
        profile.zipcode,
        profile.zipCode,
        profile.zip_code,
        profile.postalCode,
        profile.postal_code,
        profile.pincode,
        profile.pin_code,
        profile.zip,
        ''
      ) || ''
    ).replace(/\D/g, '').slice(0, 5);
    if (resolvedFreelancerZip) setFreelancerZipcode(resolvedFreelancerZip);

    const servDesc = String(pick(profile.serviceDescription, profile.description, '') || '');
    if (servDesc) setServiceDescription(servDesc);

    const freeWeb = String(pick(profile.webUrl, profile.website, profile.freelancerWebsite, '') || '');
    if (freeWeb) setFreelancerWebUrl(freeWeb);

    const freeLicState = String(pick(profile.licenseIssuedState, '') || '');
    if (freeLicState) setFreelancerLicenseIssuedState(freeLicState);
  };

  useEffect(() => {
    const data = profileData || signupData || storedUser;
    if (data && typeof data === 'object') {
      applyProfileToVerificationForm(data);
    }
  }, [profileData, signupData, storedUser]);

  // Auto-populate document fields from backend profileUploadedDocs if state is empty
  useEffect(() => {
    if (!profileUploadedDocs || profileUploadedDocs.length === 0) return;
    const lookup = buildSummaryDocLookup(profileUploadedDocs);

    const makeDocObj = (doc) => ({
      name: doc.displayName || doc.fileName || 'Document',
      displayName: doc.displayName || doc.fileName || 'Document',
      url: doc.url,
      isExisting: true,
      id: doc.id,
      lastModified: 1,
      size: 1,
    });

    const compReg = lookup['Company registration'];
    if (compReg && companyRegDoc.length === 0) {
      setCompanyRegDoc([makeDocObj(compReg)]);
    }

    const natId = lookup['National ID / Permit'];
    if (natId) {
      if (isCompany && nationalIdDoc.length === 0) {
        setNationalIdDoc([makeDocObj(natId)]);
      } else if (!isCompany && freelancerNationalIdDoc.length === 0) {
        setFreelancerNationalIdDoc([makeDocObj(natId)]);
      }
    }

    const pDoc = lookup['Photo'];
    if (pDoc && !photo && !isCompany) {
      setPhoto(makeDocObj(pDoc));
    }

    const expDoc = lookup['Experience document'];
    if (expDoc && yearOfExpDoc.length === 0 && !isCompany) {
      setYearOfExpDoc([makeDocObj(expDoc)]);
    }
  }, [profileUploadedDocs, isCompany]);

  const handleEditFromPreview = () => {
    setStep('form');
  };

  if (!signupData && !isAuthenticated && step !== 'inProgress' && step !== 'complete' && step !== 'preview') return null;

  // ── In Progress / Complete ──
  if (step === 'inProgress' || step === 'complete') {
    return (
      <>
        <GlobalStyle />
        <div className="av-root" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
          <div className="av-verify-card" style={step === 'complete' ? { background: 'linear-gradient(to bottom, #f0fdf4, #fff)' } : {}}>
            {step === 'inProgress' ? (
              <>
                <div className="av-verify-clock"><AccessTime style={{ fontSize: 38, color: '#d97706' }} /></div>
                <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#0f172a', fontFamily: "'DM Sans', sans-serif" }}>Verification in Progress</h2>
                <p style={{ margin: '12px 0 24px', fontSize: 15, color: '#64748b', fontFamily: "'DM Sans', sans-serif", lineHeight: 1.6 }}>We're reviewing your documents and information</p>
                <button
                  type="button"
                  onClick={handleLogout}
                  style={{
                    width: '100%',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    background: '#1a56db',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: 10,
                    padding: '12px 24px',
                    fontSize: 15,
                    fontWeight: 600,
                    fontFamily: "'DM Sans', sans-serif",
                    cursor: 'pointer',
                    boxShadow: '0 4px 14px rgba(26, 86, 219, 0.25)',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <Logout style={{ fontSize: 18 }} /> Log out / Return to sign in
                </button>
              </>
            ) : (
              <>
                <div className="av-verify-success"><CheckCircle style={{ fontSize: 40 }} /></div>
                <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#16a34a', fontFamily: "'DM Sans', sans-serif" }}>Verification Complete!</h2>
                <p style={{ margin: '12px 0 0', fontSize: 15, color: '#64748b', fontFamily: "'DM Sans', sans-serif" }}>Your account has been successfully verified</p>
                <div className="av-welcome-box">
                  <CheckCircle style={{ fontSize: 20, color: '#16a34a', flexShrink: 0, marginTop: 2 }} />
                  <span style={{ fontSize: 14, color: '#166534', fontFamily: "'DM Sans', sans-serif", lineHeight: 1.5 }}>Welcome to Welivin! Please sign in again to continue with your verified account.</span>
                </div>
                <p style={{ margin: '16px 0 0', fontSize: 13, color: '#94a3b8', fontFamily: "'DM Sans', sans-serif" }}>
                  For security, your session ends here. Use the button below to go to the login page.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    logout();
                    navigate('/login');
                  }}
                  style={{
                    marginTop: 24,
                    width: '100%',
                    maxWidth: 320,
                    height: 48,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: 12,
                    background: 'linear-gradient(90deg, #2563eb, #1d4ed8)',
                    color: '#fff',
                    fontSize: 16,
                    fontWeight: 600,
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  Go to login
                </button>
              </>
            )}

          </div>
        </div>
      </>
    );
  }

  // ── Preview ──
  if (step === 'preview') {
    const companyCityObj = cities.find((c) => String(c.id) === String(city));
    const companyDistrictObj = companyDistricts.find((d) => String(d.id) === String(district));
    const companyCityLabel = companyCityObj?.name || companyCityObj?.title || city || '—';
    const companyDistrictLabel = companyDistrictObj?.name || companyDistrictObj?.title || district || '—';

    const freelancerCityObj = cities.find((c) => String(c.id) === String(freelancerCity));
    const freelancerDistrictObj = freelancerDistricts.find((d) => String(d.id) === String(freelancerDistrict));
    const freelancerCityLabel = freelancerCityObj?.name || freelancerCityObj?.title || freelancerCity || '—';
    const freelancerDistrictLabel = freelancerDistrictObj?.name || freelancerDistrictObj?.title || freelancerDistrict || '—';

    const summaryDocByLabel = buildSummaryDocLookup(profileUploadedDocs);

    const renderDocItem = (title, localFiles, summaryLabel, isRequired) => {
      const profileDoc = summaryDocByLabel[summaryLabel];
      const localFileArr = Array.isArray(localFiles) ? localFiles : (localFiles ? [localFiles] : []);
      const hasLocal = localFileArr.length > 0;
      const hasProfile = Boolean(profileDoc);
      const isAttached = hasLocal || hasProfile;

      const fileNameText = hasLocal
        ? localFileArr.map((f) => f.name || f.displayName || f.fileName || 'Document').join(', ')
        : hasProfile
        ? profileDoc.displayName || profileDoc.fileName
        : 'No document attached';

      const getPreviewDocs = () => {
        if (hasLocal) {
          return localFileArr
            .map((f, idx) => {
              if (!f) return null;
              if (f.url) {
                return {
                  id: f.id || `local-${idx}`,
                  url: f.url,
                  displayName: f.displayName || f.name || `${title} (${idx + 1})`,
                };
              }
              if (f instanceof File) {
                return {
                  id: `file-${idx}-${f.name}`,
                  url: URL.createObjectURL(f),
                  displayName: f.name || `${title} (${idx + 1})`,
                };
              }
              return null;
            })
            .filter(Boolean);
        }
        if (hasProfile) {
          return [profileDoc];
        }
        return [];
      };

      const previewObjs = getPreviewDocs();

      return (
        <div key={title} className="av-doc-item">
          <div className="av-doc-left">
            <div className="av-doc-icon-badge">
              <Description style={{ fontSize: 20 }} />
            </div>
            <div className="av-doc-meta">
              <div className="av-doc-title">
                {title} {isRequired && <span style={{ color: '#dc2626', fontWeight: 700, fontSize: 14 }}>*</span>}
              </div>
              <div className="av-doc-filename">
                {fileNameText}
              </div>
            </div>
          </div>
          <div className="av-doc-right" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {isAttached ? (
              <span className="av-status-pill success">
                <CheckCircle style={{ fontSize: 14 }} /> Attached
              </span>
            ) : (
              <span className="av-status-pill neutral">
                Not Provided
              </span>
            )}

            {previewObjs.map((pObj, idx) => (
              <button
                key={pObj.id || pObj.url || idx}
                type="button"
                onClick={() => setVerificationPreviewDoc(pObj)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 14px',
                  borderRadius: 8,
                  border: '1px solid #cbd5e1',
                  background: '#ffffff',
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#1a56db',
                  cursor: 'pointer',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                }}
              >
                <Description style={{ fontSize: 14 }} />
                {previewObjs.length > 1 ? `Document ${idx + 1}` : 'Document'}
              </button>
            ))}
          </div>
        </div>
      );
    };

    return (
      <>
        <GlobalStyle />
        <div className="av-root">
          <div className="av-wrapper">
            {/* Hero Banner */}
            <div className="av-hero">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
                <div>
                  <div className="av-hero-badge"><Shield style={{ fontSize: 12 }} /> Review & Confirm</div>
                  <h1 className="av-hero-title">Verify Your Information</h1>
                  <p className="av-hero-sub">Please review all submitted details and accept the consent declarations below before final submission.</p>
                </div>
                <button
                  type="button"
                  onClick={handleLogout}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    background: 'rgba(255, 255, 255, 0.15)',
                    border: '1px solid rgba(255, 255, 255, 0.25)',
                    color: '#ffffff',
                    borderRadius: 8,
                    padding: '7px 16px',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    backdropFilter: 'blur(4px)',
                  }}
                >
                  <Logout style={{ fontSize: 16 }} /> Log out
                </button>
              </div>
            </div>

            {/* Stepper Progress Bar */}
            <div className="av-stepper-bar">
              <div className="av-step-item completed">
                <div className="av-step-badge">✓</div>
                <div className="av-step-label">
                  <span className="av-step-num">Step 1</span>
                  <span className="av-step-title">Upload Credentials</span>
                </div>
              </div>
              <div className="av-step-divider completed"></div>
              <div className="av-step-item active">
                <div className="av-step-badge">2</div>
                <div className="av-step-label">
                  <span className="av-step-num">Step 2</span>
                  <span className="av-step-title">Review & Confirm</span>
                </div>
              </div>
              <div className="av-step-divider"></div>
              <div className="av-step-item pending">
                <div className="av-step-badge">3</div>
                <div className="av-step-label">
                  <span className="av-step-num">Step 3</span>
                  <span className="av-step-title">Verification Status</span>
                </div>
              </div>
            </div>

            {/* Main Preview Cards Grid */}
            <div className="av-preview-section">

              {/* Card 1: Profile Details */}
              <div className="av-preview-card-v2">
                <div className="av-preview-card-header">
                  <div className="av-preview-card-title">
                    <div className="av-preview-card-icon">
                      {isCompany ? <Business style={{ fontSize: 18 }} /> : <Person style={{ fontSize: 18 }} />}
                    </div>
                    Profile & Account Details
                  </div>
                </div>
                <div className="av-preview-card-body">
                  <div className="av-details-grid">
                    <div className="av-detail-item">
                      <span className="av-detail-label">Account Type</span>
                      <span className="av-detail-value">
                        <span className="av-type-chip">{resolvedAccountType || (isCompany ? 'Company' : 'Freelancer')}</span>
                      </span>
                    </div>
                    <div className="av-detail-item">
                      <span className="av-detail-label">{isCompany ? 'Business Name' : 'Full Name'}</span>
                      <span className="av-detail-value">{displayName || '—'}</span>
                    </div>
                    {isCompany && (
                      <div className="av-detail-item">
                        <span className="av-detail-label">Name</span>
                        <span className="av-detail-value">{displayOwnerName || '—'}</span>
                      </div>
                    )}
                    <div className="av-detail-item">
                      <span className="av-detail-label">Email Address</span>
                      <span className="av-detail-value">{profileEmail || '—'}</span>
                    </div>
                    <div className="av-detail-item">
                      <span className="av-detail-label">Service Type / Category</span>
                      <span className="av-detail-value">{profileServiceType || '—'}</span>
                    </div>
                    {profileCountry && (
                      <div className="av-detail-item">
                        <span className="av-detail-label">Country</span>
                        <span className="av-detail-value">{profileCountry}</span>
                      </div>
                    )}
                    {(isCompany ? webUrl : freelancerWebUrl) && (
                      <div className="av-detail-item">
                        <span className="av-detail-label">Website</span>
                        <span className="av-detail-value">{isCompany ? webUrl : freelancerWebUrl}</span>
                      </div>
                    )}
                  </div>
                  {(isCompany ? companyDescription : serviceDescription) && (
                    <div className="av-detail-item" style={{ marginTop: 16 }}>
                      <span className="av-detail-label">Description</span>
                      <span className="av-detail-value" style={{ fontWeight: 400, fontSize: 13, lineHeight: 1.5 }}>
                        {isCompany ? companyDescription : serviceDescription}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Card 2: Registered Location & Address */}
              <div className="av-preview-card-v2">
                <div className="av-preview-card-header">
                  <div className="av-preview-card-title">
                    <div className="av-preview-card-icon">
                      <Place style={{ fontSize: 18 }} />
                    </div>
                    Registered Location & Address
                  </div>
                </div>
                <div className="av-preview-card-body">
                  <div className="av-details-grid">
                    <div className="av-detail-item">
                      <span className="av-detail-label">City</span>
                      <span className="av-detail-value">{isCompany ? companyCityLabel : freelancerCityLabel}</span>
                    </div>
                    <div className="av-detail-item">
                      <span className="av-detail-label">District</span>
                      <span className="av-detail-value">{isCompany ? companyDistrictLabel : freelancerDistrictLabel}</span>
                    </div>
                    <div className="av-detail-item">
                      <span className="av-detail-label">Zipcode / Postal Code</span>
                      <span className="av-detail-value">{displayZipcode || '—'}</span>
                    </div>
                    {(isCompany ? licenseIssuedState : freelancerLicenseIssuedState) && (
                      <div className="av-detail-item">
                        <span className="av-detail-label">License Issued State</span>
                        <span className="av-detail-value">{isCompany ? licenseIssuedState : freelancerLicenseIssuedState}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Card 3: Uploaded Verification Documents */}
              <div className="av-preview-card-v2">
                <div className="av-preview-card-header">
                  <div className="av-preview-card-title">
                    <div className="av-preview-card-icon">
                      <VerifiedUser style={{ fontSize: 18 }} />
                    </div>
                    Uploaded Verification Documents
                  </div>
                </div>
                <div className="av-preview-card-body">
                  <div className="av-doc-list">
                    {isCompany ? (
                      <>
                        {renderDocItem('Proof of Business Registration / Employment Authorization', companyRegDoc, 'Company registration', true)}
                        {renderDocItem('Passport / Residence Permit', nationalIdDoc, 'National ID / Permit', true)}
                      </>
                    ) : (
                      <>
                        {renderDocItem('Photo (Live capture or upload)', photo, 'Photo', true)}
                        {renderDocItem('Passport / Residence Permit', freelancerNationalIdDoc, 'National ID / Permit', true)}
                        {renderDocItem('Experience Document / Proof', yearOfExpDoc, 'Experience document', true)}
                      </>
                    )}
                  </div>
                </div>
              </div>

            </div>

            {/* Document Preview Modal */}
            <DocumentPreviewDialog viewer={verificationPreviewDoc} onClose={() => setVerificationPreviewDoc(null)} />

            {/* Declaration & Consent Box */}
            <div className="av-consent-card">
              <div className="av-consent-header">
                <Shield style={{ color: '#1d4ed8', fontSize: 20 }} />
                <h3 className="av-consent-title">Declaration & Legal Consent</h3>
              </div>
              <div className="av-consent-item">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={termsAndConditionsAccepted}
                      onChange={(e) => setTermsAndConditionsAccepted(e.target.checked)}
                      color="primary"
                      size="small"
                      sx={{ pt: 0.2 }}
                    />
                  }
                  label={
                    <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: '#1e293b', lineHeight: 1.5 }}>
                      I confirm that all details and documents provided above are accurate and valid. I agree to the <strong>Terms & Conditions</strong>. <strong style={{ color: '#dc2626' }}>*Required</strong>
                    </span>
                  }
                  style={{ margin: 0, alignItems: 'flex-start' }}
                />
              </div>
              <div className="av-consent-item">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={dataProcessingConsent}
                      onChange={(e) => setDataProcessingConsent(e.target.checked)}
                      color="primary"
                      size="small"
                      sx={{ pt: 0.2 }}
                    />
                  }
                  label={
                    <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: '#1e293b', lineHeight: 1.5 }}>
                      I consent to the processing of my data for verification purposes and I agree to the <strong>Privacy Policy</strong>. <strong style={{ color: '#dc2626' }}>*Required</strong>
                    </span>
                  }
                  style={{ margin: 0, alignItems: 'flex-start' }}
                />
              </div>

              {!canConfirmSubmission && (
                <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#1d4ed8', background: '#eff6ff', padding: '8px 12px', borderRadius: 8, border: '1px solid #dbeafe' }}>
                  <Info style={{ fontSize: 16, flexShrink: 0 }} />
                  Please check both required consent boxes above to enable final submission.
                </div>
              )}
            </div>

            {/* Bottom Actions Bar */}
            <div className="av-submit-area">
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a', fontFamily: "'DM Sans', sans-serif" }}>Ready to submit?</div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2, fontFamily: "'DM Sans', sans-serif" }}>
                  {canConfirmSubmission ? "All requirements met. Click submit to proceed with verification." : "Accept the legal consents above to unlock final submission."}
                </div>
              </div>
              <Box display="flex" gap={2} className="av-submit-actions">
                <button type="button" className="av-submit-btn av-submit-btn-outline" onClick={() => void handleEditFromPreview()} disabled={docsSubmitting}>
                  <ArrowBack style={{ fontSize: 15 }} /> Edit Details
                </button>
                <button className="av-submit-btn" onClick={handleSubmit} disabled={!canConfirmSubmission || docsSubmitting}>
                  {docsSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" style={{ width: 14, height: 14, borderWidth: 2 }} />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit for Verification <ArrowForward style={{ fontSize: 15 }} />
                    </>
                  )}
                </button>
              </Box>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── Form step ──
  return (
    <>
      <GlobalStyle />
      <div className="av-root">
        <div className="av-wrapper">
          {/* Hero */}
          <div className="av-hero">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
              <div>
                <div className="av-hero-badge"><Shield style={{ fontSize: 12 }} /> Identity Verification</div>
                <h1 className="av-hero-title">Account Verification Required</h1>
                <p className="av-hero-sub">To ensure security and authenticity, we need to verify your professional credentials.</p>
              </div>
              <button
                type="button"
                onClick={handleLogout}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  background: 'rgba(255, 255, 255, 0.15)',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  color: '#ffffff',
                  borderRadius: 8,
                  padding: '7px 16px',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  backdropFilter: 'blur(4px)',
                }}
              >
                <Logout style={{ fontSize: 16 }} /> Log out
              </button>
            </div>
          </div>
          {/* Stepper Progress Bar */}
          <div className="av-stepper-bar">
            <div className="av-step-item active">
              <div className="av-step-badge">1</div>
              <div className="av-step-label">
                <span className="av-step-num">Step 1</span>
                <span className="av-step-title">Upload Credentials</span>
              </div>
            </div>
            <div className="av-step-divider"></div>
            <div className="av-step-item pending">
              <div className="av-step-badge">2</div>
              <div className="av-step-label">
                <span className="av-step-num">Step 2</span>
                <span className="av-step-title">Review & Confirm</span>
              </div>
            </div>
            <div className="av-step-divider"></div>
            <div className="av-step-item pending">
              <div className="av-step-badge">3</div>
              <div className="av-step-label">
                <span className="av-step-num">Step 3</span>
                <span className="av-step-title">Verification Status</span>
              </div>
            </div>
          </div>

          {/* Yellow Note Alert Banner */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              fontSize: 13,
              color: '#92400e',
              background: '#fef3c7',
              padding: '12px 18px',
              borderRadius: 12,
              border: '1px solid #fde68a',
              marginBottom: 20,
              boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
            }}
          >
            <Info style={{ fontSize: 18, color: '#d97706', flexShrink: 0 }} />
            <span><strong>Note:</strong> Please do not refresh until document submission is complete.</span>
          </div>

          {/* Two-column layout */}
          <div className="av-layout">
            {/* ── Left sidebar ── */}
            <div className="av-sidebar">
              {/* Profile card */}
              <div className="av-card">
                <div className="av-card-header">
                  <div className="av-card-icon"><Person style={{ fontSize: 16, color: '#1a56db' }} /></div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#0f172a', fontFamily: "'DM Sans', sans-serif" }}>Your Profile</div>
                    <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: "'DM Sans', sans-serif" }}>Submitted at registration</div>
                  </div>
                </div>
                <div className="av-card-body">
                  {profileLoading && !profileData && (
                    <div className="av-info-row">
                      <div>
                        <div className="av-info-label">Loading profile...</div>
                      </div>
                    </div>
                  )}
                  {[
                    { icon: <Person style={{ fontSize: 13, color: '#475569' }} />, label: 'Full Name', value: displayName },
                    { icon: <Email style={{ fontSize: 13, color: '#475569' }} />, label: 'Email', value: profileEmail || '—' },
                    { icon: <Build style={{ fontSize: 13, color: '#475569' }} />, label: 'Service Type', value: profileServiceType },
                  ].map(({ icon, label, value }) => (
                    <div key={label} className="av-info-row">
                      <div className="av-info-icon">{icon}</div>
                      <div>
                        <div className="av-info-label">{label}</div>
                        <div className="av-info-value">{value}</div>
                      </div>
                    </div>
                  ))}
                  <div className="av-info-row">
                    <div className="av-info-icon"><Business style={{ fontSize: 13, color: '#475569' }} /></div>
                    <div>
                      <div className="av-info-label">Account Type</div>
                      <div style={{ marginTop: 4 }}><span className="av-type-chip">{resolvedAccountType || '—'}</span></div>
                    </div>
                  </div>
                  {profileCountry && (
                    <div className="av-info-row">
                      <div className="av-info-icon"><Place style={{ fontSize: 13, color: '#475569' }} /></div>
                      <div>
                        <div className="av-info-label">Country</div>
                        <div className="av-info-value">{profileCountry}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Checklist card */}
              <div className="av-checklist-card">
                <div className="av-checklist-title">
                  <VerifiedUser style={{ color: '#16a34a', fontSize: 16 }} />
                  Verification Checklist
                </div>
                {(isCompany
                  ? [
                      'Proof of Business Registration / Employment Authorization *',
                      'Passport / Residence Permit *'
                    ]
                  : [
                      'Photo (live or upload) *',
                      'Passport / Residence Permit *',
                      'Experience proof *'
                    ]
                ).map((item) => {
                  const isReq = item.includes('*');
                  const cleanText = item.replace('*', '').trim();
                  return (
                    <div key={item} className="av-req-item">
                      <CheckCircle style={{ fontSize: 13, color: '#16a34a', flexShrink: 0 }} />
                      <span>{cleanText} {isReq && <span style={{ color: '#dc2626', fontWeight: 700 }}>*</span>}</span>
                    </div>
                  );
                })}
                <div className="av-meta-row">
                  <div className="av-meta-item"><AccessTime style={{ fontSize: 13 }} /> Review: 2–3 business days</div>
                  <div className="av-meta-item"><Email style={{ fontSize: 13 }} /> Email on completion</div>
                  <div className="av-meta-item"><Person style={{ fontSize: 13 }} /> Full access after approval</div>
                </div>
              </div>

              {/* Help Center card */}
              <div className="av-help-card">
                <div className="av-help-title">
                  <HeadsetMic style={{ color: '#0284c7', fontSize: 17 }} />
                  Help Center
                </div>
                <div className="av-help-sub">
                  Need assistance with your account verification? Contact support:
                </div>
                <a href="mailto:info@welivin.app" className="av-help-item">
                  <Email style={{ fontSize: 15, color: '#0284c7' }} />
                  info@welivin.app
                </a>
                <a href="tel:+49017658691340" className="av-help-item">
                  <Phone style={{ fontSize: 15, color: '#0284c7' }} />
                  +49 (0176) 58691340
                </a>
              </div>
            </div>

            {/* ── Right: Form card ── */}
            <div>
              <div className="av-form-card">
                <div className="av-form-card-header">
                  <div className="av-card-icon"><Description style={{ fontSize: 16, color: '#1a56db' }} /></div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#0f172a', fontFamily: "'DM Sans', sans-serif" }}>Required Documents</div>
                    <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: "'DM Sans', sans-serif" }}>All fields marked * are mandatory</div>
                  </div>
                </div>
                <div className="av-form-card-body">
                  {isCompany ? (
                    <>
                      <div className="av-field-wrapper">
                        <TextField
                          label="Name"
                          required
                          value={ownerName}
                          onChange={(e) => setOwnerName(e.target.value)}
                          fullWidth
                          size="small"
                          sx={textFieldSx}
                          error={ownerName.length > 25}
                          helperText={ownerName.length > 25 ? 'Maximum 25 characters allowed' : ''}
                        />
                      </div>

                      <Grid container spacing={1.5} style={{ marginBottom: 14 }}>
                        <Grid item xs={12} sm={4}>
                          <FormControl fullWidth required size="small" sx={textFieldSx}>
                            <InputLabel>City</InputLabel>
                            <Select
                              value={city}
                              label="City"
                              disabled={citiesLoading}
                              onChange={(e) => {
                                const val = e.target.value;
                                const newCityId = val === '' ? '' : Number(val);
                                setCity(newCityId);
                                setDistrict('');
                              }}
                            >
                              <MenuItem value=""><em>Select City</em></MenuItem>
                              {cities.map((c) => (
                                <MenuItem key={c.id} value={Number(c.id)}>{c.name}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <FormControl fullWidth required size="small" sx={textFieldSx}>
                            <InputLabel>District</InputLabel>
                            <Select
                              value={district}
                              label="District"
                              disabled={companyDistrictsLoading}
                              onChange={(e) => {
                                const val = e.target.value;
                                setDistrict(val === '' ? '' : Number(val));
                              }}
                            >
                              {companyDistrictsLoading ? (
                                <MenuItem value="" disabled>Loading...</MenuItem>
                              ) : (
                                <MenuItem value=""><em>Select District</em></MenuItem>
                              )}
                              {companyDistricts.map((d) => (
                                <MenuItem key={d.id} value={Number(d.id)}>{d.name}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            label="Zipcode"
                            required
                            value={zipcode}
                            onChange={(e) => setZipcode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                            fullWidth
                            size="small"
                            sx={textFieldSx}
                            error={zipcode.length > 0 && zipcode.length !== 5}
                            helperText={zipcode.length > 0 && zipcode.length !== 5 ? 'Zipcode must be 5 digits' : ''}
                            inputProps={{ maxLength: 5 }}
                          />
                        </Grid>
                      </Grid>

                      <FileUploadZone label="Proof of Business Registration / Employment Authorization *" sublabel="PDF or image — Max 10MB" files={companyRegDoc} setFiles={setCompanyRegDoc} onRemove={(i) => setCompanyRegDoc((p) => p.filter((_, idx) => idx !== i))} />
                      <FileUploadZone label="Passport / Residence Permit *" sublabel="PDF or image — Max 10MB" files={nationalIdDoc} setFiles={setNationalIdDoc} onRemove={(i) => setNationalIdDoc((p) => p.filter((_, idx) => idx !== i))} />
                    </>
                  ) : (
                    <>
                      <Grid container spacing={1.5} style={{ marginBottom: 14 }}>
                        <Grid item xs={12} sm={4}>
                          <FormControl fullWidth required size="small" sx={textFieldSx}>
                            <InputLabel>City</InputLabel>
                            <Select
                              value={freelancerCity}
                              label="City"
                              disabled={citiesLoading}
                              onChange={(e) => {
                                const val = e.target.value;
                                const newCityId = val === '' ? '' : Number(val);
                                setFreelancerCity(newCityId);
                                setFreelancerDistrict('');
                              }}
                            >
                              <MenuItem value=""><em>Select City</em></MenuItem>
                              {cities.map((c) => (
                                <MenuItem key={c.id} value={Number(c.id)}>{c.name}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <FormControl fullWidth required size="small" sx={textFieldSx}>
                            <InputLabel>District</InputLabel>
                            <Select
                              value={freelancerDistrict}
                              label="District"
                              disabled={freelancerDistrictsLoading}
                              onChange={(e) => {
                                const val = e.target.value;
                                setFreelancerDistrict(val === '' ? '' : Number(val));
                              }}
                            >
                              {freelancerDistrictsLoading ? (
                                <MenuItem value="" disabled>Loading...</MenuItem>
                              ) : (
                                <MenuItem value=""><em>Select District</em></MenuItem>
                              )}
                              {freelancerDistricts.map((d) => (
                                <MenuItem key={d.id} value={Number(d.id)}>{d.name}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            label="Zipcode"
                            required
                            value={freelancerZipcode}
                            onChange={(e) => setFreelancerZipcode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                            fullWidth
                            size="small"
                            sx={textFieldSx}
                            error={freelancerZipcode.length > 0 && freelancerZipcode.length !== 5}
                            helperText={freelancerZipcode.length > 0 && freelancerZipcode.length !== 5 ? 'Zipcode must be 5 digits' : ''}
                            inputProps={{ maxLength: 5 }}
                          />
                        </Grid>
                      </Grid>

                      <PhotoCaptureZone photo={photo} setPhoto={setPhoto} />
                      <FileUploadZone label="Passport / Residence Permit *" sublabel="PDF or image — Max 10MB" files={freelancerNationalIdDoc} setFiles={setFreelancerNationalIdDoc} onRemove={(i) => setFreelancerNationalIdDoc((p) => p.filter((_, idx) => idx !== i))} />
                      <FileUploadZone label="Year of experience *" sublabel="Proof of experience — PDF or image, Max 10MB" files={yearOfExpDoc} setFiles={setYearOfExpDoc} onRemove={(i) => setYearOfExpDoc((p) => p.filter((_, idx) => idx !== i))} />
                    </>
                  )}
                </div>
              </div>

              {/* Submit bar */}
              <div className="av-submit-area">
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#0f172a', fontFamily: "'DM Sans', sans-serif" }}>Ready to proceed?</div>
                  <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2, fontFamily: "'DM Sans', sans-serif" }}>
                    {canProceedToPreview ? 'All required fields complete. Proceed to preview.' : 'Please fill in all required fields and upload documents.'}
                  </div>
                </div>
                <button className="av-submit-btn" onClick={handleProceedToPreview} disabled={!canProceedToPreview || docsSubmitting}>
                  {docsSubmitting ? 'Saving Documents...' : <>Proceed to Preview <ArrowForward style={{ fontSize: 15 }} /></>}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AccountVerification;
